// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// A machine, running (M3-37).
//
// The animation-frame loop, the session, the 3-D view, the audio and the input, wired together
// and taken apart again. **Deliberately not a React component**: none of it is rendering, all of
// it is imperative, and the one thing React must never be allowed to do to it is re-run it. A
// loop inside an effect that re-ran on a state change would build a second machine, and the two
// would fight over the canvas while both simulations advanced.
//
// So the component owns one of these, creates it once, and calls `dispose()` on unmount. What
// React renders is the canvas and the HUD; what this owns is everything with a clock in it.
//
// **The invariant, again, because this is where it would break.** The renderer never advances the
// simulation. This hands the driver the real elapsed time; the driver takes as many whole 1000 Hz
// steps as that buys and reports the leftover fraction; the fraction is the interpolation alpha.
// A phone at 30 fps and a Quest at 90 Hz therefore play the identical game.

import { readLanguage } from './i18n/language'
import { PlaySession, type PlaySessionOptions, type SessionFrame } from '../game/PlaySession'
import { BallTrail } from '../engine/ballTrail'
import { TestPlaySession, FASTEST, SLOWEST } from '../game/TestPlaySession'
import type { Game } from '../../core/ts/game/Game'
import { TraceRecorder } from '../game/TraceRecorder'
import { nextObjective, type AloudSnapshot } from './machineAloud'
import { spectatorPlan } from '../game/spectatorPacing'
import { CORE_VERSION } from '@core/replay'
import { SKIPPED_INITIALS } from '@core/game'
import { writeLastInitials } from './firstRun'
import type { ReplayTrace } from '@core/replay'
import type { MachineDefinition } from '@core/machine'
import type { LocalTrace } from '@src/net/traceStream'
import type { LocalHistory } from '../storage/history'
import type { ReplayOutbox } from '../storage/outbox'
import { readMotionIntensity, reduceFlashing } from './accessibility'
import { startHintFor } from './startHint'
import { MachineView } from '../world/MachineView'
import { MachineAudio } from '../audio/machineAudio'
import {
  applyAudioPrefs,
  nextAudioLevel,
  readAudioPrefs,
  withAudioLevel,
  withAudioMaster,
  writeAudioLevel,
  writeAudioMaster,
  writeAudioMuted,
  LEVEL_VOLUME,
  type AudioGroup,
  type AudioLevel,
  type AudioPrefs,
  withAudioMuted,
  withAudioCaptions,
  writeAudioCaptions,
} from '../audio/audioPrefs'
import { ArcadeAmbience } from '../audio/arcadeAmbience'
import type { AudioPlaybackContext } from '../audio/webAudio'
import { attachDesktopControls, type PlayControls } from '../input/desktop'
import { attachTouchControls, type TouchSurface } from '../input/touch'
import { TouchHaptics } from '../input/touchHaptics'
import { readHaptics } from './accessibility'
import { attachMotionNudge, browserMotionHost, type MotionHost } from '../input/motion'
import { dmdPainter, type Canvas2DLike, type DmdPaint } from '../fx/dmdCanvas'
import type { CameraPreset } from '../world/cameras'
import type { PhoneLayout } from './phone'
import { renderTier } from './tier'
import type { VenuePort } from '../xr/venuePort'
import { XRRuntime, type Look, type XRFrameInput, type XRFrameReport } from '../xr/XRRuntime'
import { forwardFromYaw } from '../xr/viewerRay'
import { frameInput, XRFrameSources } from '../xr/xrFrameLoop'
import { browserXRSystem } from '../xr/session'
import type { ImmersiveMode } from '../xr/features'
import type { InputMethod } from '../net/contract'
import type { MenuSelection } from '../xr/wristMenu'
import type { KeepPort } from '../xr/build/keeping'
import { VoiceMute, type MicrophoneAccess, type VoiceMuteState } from '../audio/voiceMute'
import { SpeechGate } from '../audio/voiceGate'
import { safeStorage } from '../storage/local'
import { takeBuildIntent } from '../xr/build/intent'
import { ribbonLegend, wantsANewGame, type RibbonLegendRow } from '../xr/build/testTools'
import { benchStringsFor } from '../xr/build/benchStrings'
import { recycleSetting } from '../xr/safetyFlag'
import { cabinetDimensionsFor } from '../world/cabinet'

/**
 * What the cabinet's drawn controls need, at the rate a hand moves (M3-49).
 *
 * Read off `PlayControls` rather than off the simulation, because these are the *player's* half of
 * the machine: a button has to look pressed the instant the thumb lands on it, not once the bat has
 * finished swinging. It is also what makes a keyboard, a headset trigger and a thumb light the same
 * button — there is one place a flipper is pressed and this is downstream of it.
 */
export interface ControlState {
  readonly left: boolean
  readonly right: boolean
  /** How far the plunger is drawn, 0…1. */
  readonly draw: number
  /** Is there a ball in the shooter lane for it to fire? */
  readonly plungerLive: boolean
}

/** What the HUD needs each frame. Read, never retained. */
export interface MachineReadout {
  readonly score: number
  readonly ball: number
  readonly ballsPerGame: number
  readonly player: number
  readonly players: number
  readonly phase: string
  readonly awaitingLaunch: boolean
  /** How far the plunger is drawn right now, 0…1. Zero when it is not being drawn. */
  readonly plunger: number
  readonly tilted: boolean
  readonly ballSaveSeconds: number
  /** The current callout's caption, or empty (07 §6, NFR-025). */
  readonly caption: string
  /**
   * The three characters being entered on the high-score table, and which one is being changed.
   *
   * `initialsAt` is −1 whenever the machine is not asking. Entering initials is the one moment
   * in a game where the machine is **waiting for an answer** rather than showing a result, and
   * a screen has to say so — a cabinet's flipper buttons are physical objects in front of the
   * player, and the same buttons here are two invisible rectangles.
   */
  readonly initials: string
  readonly initialsAt: number
  /** A one-off line for the player: a performance interruption, a graphics step-down. */
  readonly notice: string
  /** False until the first user gesture has been able to start an audio context. */
  readonly audible: boolean
  /** The machine's own sound is muted (07 §7). The HUD's speaker control reads it. */
  readonly audioMuted: boolean
  /**
   * The simulation is stopped (M3-55).
   *
   * **Exposed because a menu row has to say which way it is about to go**, and the flat menu's
   * first guess at this read `phase`, which is a *game* phase — `attract`, `ball`, `bonus` — and
   * is never `paused`. A toggle whose label is derived from the wrong field is the failure mode
   * `labelFor` exists to prevent, arrived at from a different direction.
   */
  readonly paused: boolean
  /**
   * Every player's score, in seat order (M8-17).
   *
   * One entry for a solo game, which is the common case and costs an array of one. Recomputed each
   * readout and compared into the signature, so a hot-seat scoreboard redraws when somebody scores
   * and not otherwise.
   */
  readonly standings: readonly PlayerStanding[]
  /**
   * This machine has a working display of its own (08 §4.2).
   *
   * **The HUD reads it to decide whether to print the score at all.** 08 §4.2 makes the machine's
   * own DMD the primary readout, "exactly as on a real machine", and the HUD's job is only what a
   * screen needs and a cabinet does not. A HUD score drawn over a backglass already showing the
   * same number is the same fact twice, in two typefaces, one on top of the other — which is what
   * it looked like on screen.
   *
   * Not every machine has a display, though: `DisplayDriver.present` is false for a definition
   * with no `display` section or no idle scene, and on one of those the HUD score is the *only*
   * readout there is. So this is published rather than assumed, and the HUD falls back rather
   * than going blank.
   */
  readonly hasDisplay: boolean
  /** This player's microphone is off (M8-09). The HUD's mute control reads it. */
  readonly micMuted: boolean
  /** Everybody else's voices are off (M8-09). */
  readonly voiceMuted: boolean
  /** What the browser said about a microphone (M8-10). */
  readonly micAccess: MicrophoneAccess
  /** The player has already been told their microphone is blocked, and dismissed it (M8-10). */
  readonly micNoticed: boolean
}

/**
 * One player's line on the backglass (M8-17).
 *
 * **All of them, every readout, rather than only the current player's.** A hot-seat game is four
 * people watching one screen, and three of them are watching to see whether they are still ahead —
 * a scoreboard that showed only whoever is holding the flippers would be the one thing on screen
 * that none of the other three can use.
 */
export interface PlayerStanding {
  /** 1-based, as a backglass counts. */
  readonly player: number
  readonly score: number
  /** Tilted on the ball currently in play. Cleared when their next ball starts. */
  readonly tilted: boolean
  readonly ballsPlayed: number
}

export interface MachineRuntimeOptions extends PlaySessionOptions {
  /** The machine, as bytes. The same bytes a server replays against. */
  readonly machine: unknown
  /** Where to draw. */
  readonly canvas: HTMLCanvasElement
  /**
   * The phone's DMD strip (M3-32, 08 §5.1), if the layout has one.
   *
   * Painted by the frame loop rather than by React, for the same reason the machine is: it
   * changes sixty times a second and re-rendering a component that often to move some dots is
   * most of the cost of the shell.
   */
  readonly dmdCanvas?: HTMLCanvasElement | null
  /**
   * What the touch controls listen on (M3-33).
   *
   * The whole stage, not the machine's canvas: the flipper zones are a band *below* the canvas in
   * the portrait layout, and a listener on the canvas would hear nothing from the one part of the
   * screen a player's thumbs are actually on.
   */
  readonly touchSurface?: TouchSurface | null
  /** Where the zones are. Read fresh on every touch — a phone rotates, and a keyboard opens. */
  readonly layout?: () => PhoneLayout
  /** The camera preset to start on. Follow on a phone, per 08 §5.1. */
  readonly camera?: CameraPreset
  /** What to attach the keyboard to. The window, in the product. */
  readonly keyboard: Window & typeof globalThis
  /** Told when something the HUD shows has changed, at most once a frame. */
  readonly onReadout?: (readout: MachineReadout) => void
  /**
   * Told when a *control* changed, which is a different rate from the readout (M3-49).
   *
   * The cabinet's drawn flipper buttons and plunger knob have to answer the hand that is on them,
   * and a bat is pressed and released several times a second — so this deliberately does **not**
   * go through `onReadout`. Re-rendering `MachineStage` on every flip is most of the cost of a
   * frame on a phone, and the plunger knob quantised to the readout's twenty steps is a knob that
   * moves in six-pixel jumps under a thumb that is moving smoothly.
   *
   * The receiver is expected to write to the DOM, not to `setState`. **Called only on a change**,
   * with the draw compared at sub-pixel resolution rather than exactly, so a knob that is being
   * held still does not produce a write per frame.
   */
  readonly onControls?: (state: ControlState) => void
  /**
   * Told when the wrist menu chose something the machine cannot do to itself (M5-24).
   *
   * `endGame` and `machine`: one unmounts this machine and one navigates away from it, and both
   * belong to whoever mounted it.
   */
  readonly onXrMenu?: (selection: MenuSelection) => void
  /**
   * Told whenever the player's mute controls change (M8-09).
   *
   * **This object owns the state and refuses to own the applying of it**, because applying it
   * means reaching the account chunk, and the account chunk is behind the FR-030 boundary that
   * `machineRuntime.ts` is on the front-door side of. The shell does the dynamic import, exactly
   * as it does for the arcade itself.
   */
  readonly onVoiceMute?: (state: VoiceMuteState) => void
  /**
   * Told when this machine starts and stops talking (M8-13).
   *
   * 07 §7's outbound voice gate: while the machine speaks, the player's outgoing microphone is
   * shut so five other people do not hear a delayed copy of a callout they can already hear from
   * its own bay. **Told only on a change**, not every frame — this runs at 72–120 Hz and the
   * answer changes a couple of times a ball.
   *
   * Same boundary as `onVoiceMute` and for the same reason: the microphone lives behind the
   * lazily imported account chunk and this module is on the front door.
   */
  readonly onVoiceGate?: (closed: boolean) => void
  /**
   * How the headset's Keep reaches the arcade (M13-20, 11 §4).
   *
   * Same boundary as `onVoiceMute` and for the same reason: the API client lives behind the
   * lazily imported account chunk and this module is on the front door, so the shell answers this
   * at the press through `import('../account')`, with the client and whether there is an account
   * to keep into. Absent — the harness, a ghost, the Builder's own test play — and the Keep row
   * says the arcade cannot be reached, which is the truth of a build with no backend.
   */
  readonly keep?: KeepPort
  /**
   * Told on every commit, undo and redo at the bench, with the draft and whether it differs from
   * the machine Build mode entered on (M13-16). The shell keeps it the way the Builder keeps its
   * own — `saveDraft`, 1200 ms after the last change, only while `edited` — so `/builder` opens
   * what was laid out on the headset and a recycle mid-carry loses the carry and nothing else.
   * This object does not write it: the draft store is the shell's, like the history.
   */
  readonly onDraft?: (definition: MachineDefinition, edited: boolean) => void
  /**
   * Allow the Builder's test-play tools (M9-18, 11 §4).
   *
   * **Off unless asked for, and the tools refuse without it.** 11 §4: freeze, single-step, place
   * ball and the rest are "available only in the Builder and never in a scored game" — and that is
   * a property this object can hold rather than a rule about who calls it. A scored game whose
   * author could drop the ball onto the jackpot is not a leaderboard, and a server re-simulating
   * the input trace would find a ball that teleported.
   */
  readonly testTools?: boolean
  /** Told once, when the 3-D view could not be built. The page decides what to say. */
  readonly onRenderFailure?: (message: string) => void
  /**
   * Told the first time the player presses a flipper, so the first-run hint can retire (M3-33a).
   *
   * **A flipper, not any touch.** The hint is the only thing that ever tells a phone player where
   * the flippers are, and 08 §5.2 gives it "the first ball of a player's first game" — so retiring
   * it on a stray tap, a nudge flick or a hand resting on the glass spent it on a touch that
   * taught nobody anything, permanently and per browser.
   */
  readonly onFirstFlip?: () => void
  /**
   * Told the first time the player *pulls* the plunger, so the arrow teaching it can retire
   * (M3-51).
   *
   * Separate from `onFirstFlip`, and that separation is the fix. One flag used to retire both
   * halves of the hint, so a player who tapped a flipper on ball one lost the plunger lesson they
   * had never been given — the two gestures are learned independently and have to be forgotten
   * independently.
   */
  readonly onFirstPlunge?: () => void
  /**
   * The plunger was pulled with a finger — every time, not only the first (M3-45).
   *
   * **Separate from `onFirstPlunge`, and the separation is the point.** That one retires a hint and
   * must fire once ever; this one is the moment play starts, which is true of every ball. The host
   * uses it to give the machine the screen.
   *
   * Fires synchronously inside `touch.ts`'s `touchend` listener, so a handler may ask for
   * fullscreen — the gesture is still live. A mouse never fires `touchstart`, so this is the finger
   * path and nothing else reaches it.
   */
  readonly onPlunge?: () => void
  /**
   * Where device-motion nudges come from (M3-34). The window, in the product.
   *
   * Named rather than assumed so a test can shove a phone that is not there, and `undefined`
   * where the browser has no sensor API at all — in which case `setMotionEnabled` is a no-op
   * and the offer is never made.
   */
  readonly motionHost?: MotionHost | undefined
  /** Start with device-motion nudging on. The stored answer to the offer, in the product. */
  readonly motionNudge?: boolean
  /**
   * Where finished games, scores and the interrupted game are kept (M3-39).
   *
   * A **provider**, like the machine and the lamp state, and for a sharper reason than either:
   * opening IndexedDB is asynchronous and building the machine must not wait for it. Passed as a
   * value instead, the history arriving a moment after load would change the option and rebuild
   * the whole machine — which is exactly what happened when this was first written, and what it
   * looked like was a game that silently restarted itself a second after the player pressed the
   * plunger.
   *
   * Optional, and absent is a working configuration rather than a degraded one: the harness has
   * no use for a history and the arcade's attract tiles must not write one. A machine with no
   * history plays exactly the same game; it simply forgets it.
   */
  readonly history?: () => LocalHistory | undefined
  /**
   * Where a finished game goes to be posted.
   *
   * Separate from `history` because they answer different questions and fail differently: the
   * history is what this browser remembers, and the outbox is a promise to a leaderboard. A page
   * that has one and not the other is a real state — the settings page reads history without ever
   * wanting to post anything.
   */
  readonly outbox?: () => ReplayOutbox | undefined
  /** A recorded game to fast-forward into before play starts. See `PlaySession.resume`. */
  readonly resumeFrom?: ReplayTrace
  /**
   * Where initials entry opens, when it should not be 'AAA' (M2-19a).
   *
   * The browser's memory of what this player last typed, handed in rather than read here — the
   * runtime is the layer that owns `localStorage`, but the *decision* about which value a game is
   * played with belongs to the caller, which is the only one that knows whether this is a fresh
   * game or a resumed one. See `startingInitialsFor`.
   */
  readonly startingInitials?: string
  /**
   * Play a recording back instead of playing (M4-21).
   *
   * The machine, the renderer, the camera, the lamps and the DMD are all the same — a ghost is
   * the same machine driven by a trace instead of by hands, which is why this is an option on the
   * runtime rather than a second renderer. The controls are the difference, and they are read
   * through functions so that changing the speed does not rebuild the machine.
   */
  readonly ghost?: {
    readonly trace: ReplayTrace
    /** A multiplier on real time. 1 is the pace it was played at; 0 is paused. */
    readonly speed: () => number
    /** A step to jump to, taken and cleared. `undefined` on the frames nobody scrubbed. */
    readonly takeSeek: () => number | undefined
    /** Told each frame how far through it is, so a scrub bar can follow. */
    readonly onProgress?: (step: number, steps: number) => void
  }
  /** Told when a finished game has been written down, and whether it made the local table. */
  readonly onSaved?: (score: number, madeTheTable: boolean) => void
  /**
   * Told the first time a device shove lands, so the page can confirm the sensor works.
   *
   * Worth a callback of its own because this is the one control in the product whose failure is
   * *silent*: a browser can grant the permission and then emit nothing — no sensor on the
   * device, a permissions policy on the frame, a desktop that has the API and no hardware — and
   * a player who turned motion nudging on and got nothing has no way to tell that from being
   * bad at shoving.
   */
  readonly onMotionShove?: () => void
}

export class MachineRuntime {
  readonly session: PlaySession
  readonly controls: PlayControls

  private view: MachineView | undefined
  private audio: MachineAudio | undefined
  private audioContext: AudioContext | undefined
  private audioTried = false
  /**
   * The player's audio levels and mute-all (07 §7).
   *
   * **Read once, at construction, and not on every access.** A getter that read `localStorage`
   * each time would be called from `publish`, which runs every frame — and `localStorage` is
   * synchronous, main-thread and occasionally slow enough to be a frame. It is also the state a
   * mixer that does not exist yet will be built from, which a getter cannot be.
   */
  private audioPrefs: AudioPrefs = readAudioPrefs()
  /**
   * The player's mute controls (M8-09).
   *
   * **Owned here, and here is the only place that owns them.** The wrist menu draws them, the HUD
   * draws them, and the account chunk applies them; three readers of one state is exactly the
   * shape that drifts, and the one it must not drift on is the microphone. Built before anything
   * else in the constructor, because the answer it loads from storage has to be in hand before an
   * arcade can be joined.
   */
  readonly voice = new VoiceMute(safeStorage())
  /** Stops the subscription in `dispose`. */
  private detachVoiceMute: () => void = () => {}
  /**
   * 07 §7's outbound voice gate (M8-13).
   *
   * Owned here because this is the object that has both halves in hand every frame — what the
   * audio layer is saying, and a way to reach the transport. Neither of those knows about the
   * other, and neither should.
   */
  private readonly speechGate = new SpeechGate()
  /** What the transport was last told, so it is told only when the answer changes. */
  private gateClosed = false

  private readonly options: MachineRuntimeOptions
  private readonly detachKeyboard: () => void
  private readonly detachGesture: () => void
  private readonly detachTouch: () => void
  private readonly motionHost: MotionHost | undefined
  private detachMotion: (() => void) | undefined
  private dmd: DmdPaint | undefined
  private dmdContext: Canvas2DLike | undefined
  /** How far a *touch* has drawn the plunger. Zero when the drag is the keyboard's. */
  private touchDraw = 0
  private frameHandle = 0
  private lastFrame = -1
  /**
   * The headset, when there is one (M5).
   *
   * Undefined when the 3-D view could not be built, because everything XR does needs a renderer
   * and a scene. A browser with no WebXR still gets one of these — it simply answers `false` to
   * every `supports`, which is the same answer the button needs either way.
   */
  private xr: XRRuntime | undefined
  private readonly xrSources = new XRFrameSources()
  /**
   * The arcade's noise floor (M5-22). VR only, and built on the first VR frame that has sound.
   *
   * Not in MR, and that is the whole reason it is here rather than inside `MachineAudio`: mixed
   * reality puts the machine in the player's own room, where the room's noise floor is the room's
   * — and generating a second one over the top of it is the most obviously wrong thing the
   * product could do in passthrough.
   */
  private ambience: ArcadeAmbience | undefined
  /** Told after every XR frame, so the shell can draw the reticle and say the toasts. */
  private xrReport: XRFrameReport | undefined
  private disposed = false
  /** The graphics step-down's line, and when it stops being said. */
  private oneOff = ''
  private oneOffUntil = 0

  /**
   * Whether the player has been told their recording stopped.
   *
   * A latch rather than a re-check, because `capped` stays true for the rest of the game and a
   * notice re-issued every frame is not a notice.
   */
  private saidCapped = false
  private lastReadout = ''
  /** Last frame's pause state, so a shove cannot move a machine that has been stopped. */
  private paused = false
  /**
   * What the machine's toys display (M3-07).
   *
   * Reads the engine live rather than holding values: the star's appearance is the Star Core
   * charge *of the player at the machine*, and in a four-player game that changes between balls.
   */
  private readonly toyReader = {
    value: (id: string): number =>
      this.session.game.engine.vars.get(id, this.session.game.engine.currentPlayer),
    modeActive: (id: string): boolean => this.session.game.engine.isModeActive(id),
  }
  /**
   * The player's flash-reduction answer, resolved once per machine and refreshed on demand.
   *
   * Resolved once, and once is enough: the control that changes it is on the Settings **route**,
   * and reaching it unmounts the machine. Asked per frame it would be a synchronous
   * `localStorage` read and a media query sixty times a second, to answer a question that cannot
   * change while the answer is being used.
   */
  private readonly flashReduced = reduceFlashing()

  /** The game being written down as it is played (M3-39). */
  private readonly recorder: TraceRecorder
  /**
   * The document the machine was built from before Build mode replaced it with a draft (M13-10).
   *
   * Set for exactly as long as the mode is on, and it is the whole of "is a draft running": while
   * it is set nothing this runtime plays is scored, saved or queued (`remember`), and leaving the
   * mode is `session.rebuild` on it. Held here rather than in the XR runtime because the session
   * is this object's and the original bytes are what it was constructed with.
   */
  private buildOriginal: unknown | undefined
  /** Wall-clock ms of the last in-progress save, so the interrupted game is never far behind. */
  private savedAt = -Infinity
  /** Was `isOver` true last frame? The edge is when a game is written down. */
  private wasOver = false
  /** The last initials the player was seen typing, for the local table's row. */
  private typedInitials = ''
  /**
   * Did the player decline the table on this game (M2-19a)?
   *
   * **Because `typedInitials` cannot tell you.** It is whatever the wheels were showing on the
   * last frame of entry, and after a skip that is three characters the player deliberately did not
   * put their name to. Without this flag the row would be labelled with them and — worse — they
   * would be *remembered* as this browser's initials, so declining once would seed every future
   * game with the letters you refused to use.
   */
  private skippedInitials = false
  /** Has a finger touched this machine? Decides what the score row says it was played with. */
  private touched = false
  /**
   * Presenting frames spent on each scheme during *this* game (M9-38).
   *
   * **A count rather than a latch, because a game played half on hands and half on controllers is
   * one row on a leaderboard and the row has to say which.** Counted only while a ball is in play
   * and only while presenting: the minutes before a plunge, and the minutes after a game over with
   * the headset still on, say nothing about how the game was played.
   *
   * Cleared by `onNewGame`, like everything else about the previous game — `touched` was not, and
   * that is the same defect M8-68 recorded about the recorder: the second game of a session
   * inherited the first game's input method and submitted it.
   */
  private xrFrames: { hands: number; controllers: number } = { hands: 0, controllers: 0 }

  /**
   * The cabinet in the player's hand (M3-42).
   *
   * Constructed always and silent unless there is a motor *and* the player wants one, so nothing
   * downstream has to ask whether it exists. Its own cursor into the contact feed, exactly as the
   * audio layer has: two consumers of one ring, neither able to consume the other's contacts.
   */
  private readonly haptics = new TouchHaptics()
  private hapticCursor = 0
  /** The tilt state last seen, so a buzz fires on the edge rather than for as long as it lasts. */
  private wasTilted = false
  /** Balls drained last frame, so a drain is felt once. */
  private drainedSeen = 0
  /** Which bats were up last frame, so a click fires on the press and not for the whole hold. */
  private flipperWas = { left: false, right: false }
  /** Whether a ball was sitting in the lane last frame, which is how a launch is detected. */
  private awaitingWas = false
  /** The last control state handed out, so `onControls` fires on a change and not on a frame. */
  private controlsWas: ControlState | undefined
  /** Flipper taps to let go of on the next frame. See `tapFlipper`. */
  private readonly tapRelease: ('left' | 'right')[] = []
  /** A button press that still owes the player a ball. See `launch`. */
  private pendingLaunch: { power: number; framesLeft: number } | undefined
  /**
   * Somebody else's ball, playing on this machine (M8-19).
   *
   * `generation` is the trace's, from `SpectatorTrace`: a change of it means a different game —
   * the turn passed — and the machine is rebuilt rather than extended.
   */
  private watching: { generation: number; liveSteps: number } | undefined

  /**
   * The last three seconds of the ball's path (M9-19), when the Builder asked for the tools.
   *
   * `undefined` outside the Builder rather than an empty one: this records a point every
   * millisecond, and a scored game has no use for it at all.
   */
  private trailNow: BallTrail | undefined

  /** The ball's path, a point per step — the Builder's, or the bench's while a draft is under test (M13-25). */
  get trail(): BallTrail | undefined {
    return this.trailNow
  }

  /**
   * A draft is under Test play on the bench (M13-25): the Builder's test tools are allowed on
   * this session for as long as it is, and refused the moment it is not. A draft game is never
   * scored (`remember`), which is what makes this the same gate as `testTools` and not a hole in it.
   */
  private buildTest = false

  /** How fast the bench's test runs, 0.1 to 1: the frame clock's, applied as `TestPlaySession` applies its own. */
  private buildTimeScale = 1

  /** Whether the ribbon is drawn while a draft is under test. */
  private ribbonOn = false
  /** The ribbon's key in the player's language, kept until the language changes. */
  private ribbonKey: { readonly language: string; readonly rows: readonly RibbonLegendRow[] } | undefined

  /** The Builder's option, or the bench's test. */
  private get toolsAllowed(): boolean {
    return this.options.testTools === true || this.buildTest
  }
  /** The contact count at the previous step, so a step can be told it touched something. */
  private contactsSeen = 0
  /** Which ball the trail is following, so a served ball starts a fresh path. */
  private trailBall = -1
  private trailX = 0
  private trailY = 0
  private trailZ = 0

  constructor(options: MachineRuntimeOptions) {
    this.options = options
    this.trailNow = options.testTools === true ? new BallTrail() : undefined
    // A resume must start in attract: the trace's own first action is the launch button being
    // pressed, because a replay reproduces what the *player* did rather than what a host did for
    // them. Starting the game first would double-start it and diverge on the first step.
    // **The Builder gets a session that can be slowed; nothing else does** (M9-23).
    // `PlaySession.timeScale` is a protected hook precisely so that a speed control cannot be one
    // line away from a scored game, and a subclass keeps it that way: a session without the tools
    // does not have the override at all.
    // **A resumed game keeps the header it was saved with**, and does not take the browser's
    // current one: the trace being adopted below already records where its entry opens, and a
    // recorder that carried on with a different value would sign the second half of the game with
    // a header the first half never had. A fresh game takes what the browser remembers.
    const startingInitials =
      options.resumeFrom !== undefined
        ? options.resumeFrom.startingInitials
        : options.startingInitials
    const Session = options.testTools === true ? TestPlaySession : PlaySession
    this.session = new Session(options.machine, {
      ...options,
      // **The browser is the only layer that knows what the controls are called here** (see
      // `DmdRenderContext.startHint`). `PlaySession` runs under a test harness and a headset too,
      // so it defaults to neutral wording and takes the answer from its host rather than
      // measuring one. A caller that has already decided keeps its own.
      startHint: options.startHint ?? startHintFor(),
      // **Always built in attract.** The recorder is constructed from the session's loaded machine,
      // so it cannot exist yet — and a game started here would start behind its back. The first
      // game is started below, through the session's recorded path, once the recorder can hear it.
      attract: true,
      ...(startingInitials === undefined ? {} : { startingInitials }),
      // Assigned below, and safe: nothing takes a player action during construction.
      onAction: (step, action) => this.recorder?.record(step, action),
      onNewGame: () => this.onNewGame(),
      // Per step, not per frame: see `PlaySessionOptions.onStep`.
      onStep: (game) => {
        this.recorder?.mark(game)
        this.markTrail(game)
      },
    })
    this.recorder = new TraceRecorder(this.session.loaded, {
      seed: options.seed ?? DEFAULT_SEED,
      playerCount: options.playerCount ?? 1,
      // **The same value the game was built with, necessarily.** A recorder writing a header the
      // simulation did not use would produce a trace that re-simulates to different initials than
      // the ones the player saw, which is precisely the divergence the field exists to prevent.
      ...(startingInitials === undefined ? {} : { startingInitials }),
    })
    if (options.ghost !== undefined) {
      this.session.beginGhost(options.ghost.trace)
    }
    if (options.resumeFrom !== undefined) {
      const result = this.session.resume(options.resumeFrom)
      // Adopted only when the fast-forward actually ran. A refused trace leaves a machine in
      // attract with an empty recorder, which is the right outcome — a fresh game — rather than
      // one that records the second half of a game it never played the first half of.
      if (result.refused === undefined) this.recorder.adopt(options.resumeFrom)
    }
    if (options.attract !== true && options.resumeFrom === undefined && options.ghost === undefined) {
      this.session.startGame(0)
    }

    const keyboard = attachDesktopControls(options.keyboard)
    this.controls = keyboard.controls
    this.detachKeyboard = keyboard.detach

    try {
      this.view = new MachineView(options.canvas, () => this.session.loaded, {
        tierChoice: renderTier(),
        dmdRaster: () => this.session.display.player.raster,
        lampState: () => (id) => this.session.game.engine.lampOf(id),
        lighting: () => this.session.lighting,
        // One object, built once. Returning a fresh one each frame would allocate sixty a
        // second for two function properties that never change.
        toyState: () => this.toyReader,
        // Read per frame, so a player who turns it on mid-game gets it on the next frame rather
        // than the next machine.
        flashReduced: () => this.flashReduced,
        events: () => this.session.events,
      })
    } catch (error) {
      // WebGL is not guaranteed — a locked-down browser, a blocklisted driver, a machine with no
      // GPU. Reported rather than thrown: the page can say so, and the simulation keeps running,
      // which matters because a game in progress is not lost by the picture going away.
      options.onRenderFailure?.(error instanceof Error ? error.message : String(error))
    }
    if (options.camera !== undefined) this.view?.setCamera(options.camera)

    // The headset (M5). Built from the same renderer and the same scene the flat view uses, and
    // pointed at the machine's own group, so entering XR *moves* the machine rather than building
    // a second one. Skipped entirely when there is no renderer, because everything it does needs
    // one — and a browser with no WebXR still gets one, because the difference between "no
    // headset" and "no picture" is a different sentence on the button.
    /*
      **Everything from here on is disposed if any of it throws** (M11-02, finding L-02).

      A constructor that fails part-way leaves what it has already built *unreachable*: React
      registers no cleanup for an effect body that threw, so the caller has nothing to call
      `dispose()` on, and the `MachineView` above holds a WebGL context and a media-query
      listener for the life of the page.

      Found by the leak audit rather than reasoned about: it measured exactly one view per cycle
      constructed and never disposed under React's StrictMode, where the second construction runs
      against the same `<canvas>` — and `getContext` on a canvas returns the *same* context,
      which is precisely the situation that makes this constructor throw.

      `dispose()` is safe on a half-built runtime: every field it touches is optional-chained and
      it refuses to run twice. So the failure path is **give back what exists, then let the error
      out** — the caller still sees the throw and the shell still shows whatever it shows when a
      machine cannot be built. What it no longer does is keep the wreckage.
    */
    try {
      const view = this.view
      if (view !== undefined) {
        this.xr = new XRRuntime({
          renderer: view.host.renderer,
          scene: view.host.scene,
          machine: view.scene.root,
          dimensions: cabinetDimensionsFor(this.session.loaded.definition),
          xr: browserXRSystem(),
          session: () => view.host.renderer.xr.getSession(),
          onMenu: (selection) => this.onXrMenu(selection),
          onEnd: () => this.onXrEnded(),
          // The XR-030 recycle, as this build sets it — and off unless it does (`safetyFlag.ts`,
          // 13 Sep 2026): the Horizon defect it guards against is not on the current build, and
          // a player is not interrupted every 165 seconds on the strength of one that was.
          ...xrSafety(),
          // Build mode's seam into the game (M13-10, ADR 0015). The XR runtime owns the hands,
          // the draft and the bench; this object owns the session, so the draft becomes the
          // machine *here* — one object graph for the picture and the simulation — and a draft
          // game is never scored (see `remember`).
          build: {
            definition: () => this.session.loaded.definition,
            // The player's language, for the bench's own words (M13-26).
            language: () => readLanguage(),
            playfield: () => view.scene.playfield,
            enter: () => this.enterBuild(),
            adopt: (definition, edited) => {
              this.adoptDraft(definition)
              this.options.onDraft?.(definition, edited)
            },
            ghost: (device, shift) => view.scene.ghost(device, shift),
            lift: (deviceId) => view.scene.lift(deviceId),
            dropGhost: (floorUnder) => view.scene.dropGhost(floorUnder),
            advanceDrops: (seconds, clear) => (clear === true ? view.scene.clearDrops() : view.scene.advanceDrops(seconds)),
            highlight: (deviceId) => view.scene.highlight(deviceId),
            // The snap's guides and the click half of its twin (M13-13).
            guides: (snapView, seconds, flash) => view.scene.guides(snapView, seconds, flash),
            click: (x, y) => {
              this.audio?.click(x, y)
            },
            // The report on the machine (M13-17).
            validation: (validationView, scale) => view.scene.validation(validationView, scale),
            // The inspector card beside the selected part (M13-22).
            inspector: (inspectorView, scale, held) => view.scene.inspector(inspectorView, scale, held),
            // The mini-map with the nudge pads (M13-23).
            map: (mapView, scale) => view.scene.miniMap(mapView, scale),
            // The selected ramp's control points, and the curve while one is in hand (M13-24).
            points: (pointsView, scale) => view.scene.rampPoints(pointsView, scale),
            // The Builder's test tools, on the draft's session while it is under test (M13-25).
            tools: {
              begin: () => this.beginBuildTest(),
              end: () => this.endBuildTest(),
              frozen: () => this.frozen,
              freeze: () => this.togglePause(),
              step: () => this.stepOnce(),
              slow: (scale) => {
                this.timeScale = scale
              },
              placeBall: (x, y) => this.placeBall(x, y),
              addBall: (x, y) => this.addBall(x, y),
              ribbon: (on) => {
                this.ribbonOn = on
              },
            },
            leave: () => this.leaveBuild(),
            // The Builder's door left a word on this tab (M13): start at the bench, once.
            wanted: () => takeBuildIntent(),
            // Keep (M13-20), through whoever mounted this machine — see the option.
            ...(this.options.keep === undefined ? {} : { keep: this.options.keep }),
          },
        })
        // Carried across at construction, not only on change. A player who muted the machine on
        // the phone and then put a headset on would otherwise meet a wrist row saying "Sound on"
        // over a machine making no noise — which is the same defect the voice subscription below
        // is ordered the way it is to avoid.
        this.xr.audioMuted = this.audioPrefs.muted
        // The Sound page's other two conditions, carried across in the same breath and for the
        // same reason: a player who turned captions off on a phone and then put a headset on would
        // otherwise open that page onto a row claiming they were on.
        this.xr.audioMaster = this.audioPrefs.master
        this.xr.audioCaptions = this.audioPrefs.captions
      }
      // **Subscribed after the XR runtime exists**, so the immediate call that `subscribe` makes —
      // the one that carries the state loaded from storage — reaches the wrist panel rather than
      // landing on an `undefined` and leaving a player who muted themselves last session looking at
      // a row that says they are live.
      /*
        **The first delivery is not a change, and crossing the boundary for it costs a visitor the
        whole account chunk** (M11-01).

        `subscribe` calls back immediately with the state loaded from storage — deliberately, so the
        wrist panel starts correct. But the *outward* half of this callback is a dynamic
        `import('../account')`, and firing it at construction means **every visitor on the front
        door downloads and parses the account chunk before touching anything**. Measured: requested
        at 171 ms on a cold load with no interaction, ~55 KB nobody was going to run.
        `check-anonymous.mjs` said the only way to download it was to navigate to `/account`; that
        was true of the module graph and not of the running page.

        Nothing is lost by holding it back. The value came from local storage, and `arcadePresence`
        reads the same storage when it starts a membership — so the arcade still opens with the mute
        the player left set, and it learns it without a machine on the front door having to tell it.
      */
      let announcedVoice = false

      this.detachVoiceMute = this.voice.subscribe((state) => {
        if (this.xr !== undefined) this.xr.voice = state
        // **Only the two choices go outward.** `access` is something the account chunk told *us*;
        // sending it back would be a round trip that can only ever restate what the other end
        // already knows, and a second place for the two to disagree.
        if (!announcedVoice) {
          announcedVoice = true
          return
        }
        options.onVoiceMute?.(state.mute)
      })

      // The stored taste control (M3-40). `MotionPreference` still multiplies by the system's
      // `prefers-reduced-motion`, so a player who asked the operating system for none gets none
      // whatever this says.
      if (this.view !== undefined) this.view.motion.intensity = readMotionIntensity()

      // The haptics preference (M3-42). Read here rather than watched, and that is not a
      // compromise: Settings and a running machine are different routes, so a player cannot be in
      // both at once and there is no live change to miss.
      this.haptics.setEnabled(readHaptics())

      const surface = options.touchSurface
      const layout = options.layout
      if (surface !== undefined && surface !== null && layout !== undefined) {
        const touch = attachTouchControls(surface, {
          controls: this.controls,
          layout,
          /**
           * **The plunger is also the start button** (M3-51).
           *
           * It used to be live only with a ball in the lane, which left the one moment that
           * matters most — a stranger on the landing route, machine in attract — with an inert
           * plunger and a button laid across the playfield as the only way in. So the column is
           * live in attract too, and `onPlunge` below routes the release through the same control
           * `Game.pressLaunchButton` calls "three buttons wired to one".
           *
           * Not live once a ball is actually moving: a drag in the bottom-right corner mid-ball is
           * a player reaching for a flipper, not asking for another ball.
           */
          plungerAvailable: () => this.plungerLive,
          /**
           * A pulled plunger, spent the way a pressed launch button is spent.
           *
           * In attract this starts the game *and* fires the ball it seats, because `launch` holds
           * the press for the frames it takes the machine to put one in the lane — the same
           * machinery the overlay button has always used. **With the drawn power, not full**: the
           * player pulled a specific distance and the skill shot is the whole point of the gesture.
           */
          onPlunge: (power) => {
            this.launch(power)
            // **After the launch, never before it.** The host's handler collapses the shell's rows
            // and may ask for fullscreen, both of which are layout; doing that first would put a
            // reflow between the player's release and the ball leaving the lane.
            this.options.onPlunge?.()
          },
          onPlungerDraw: (power) => {
            this.touchDraw = power
          },
          // Any touch: this is what makes the score row say `touch` rather than `keyboard`, and a
          // game played with the plunger button and nudges is still played with a finger.
          onFirstTouch: () => {
            this.touched = true
          },
          // A flipper press, which is the only touch that proves the zone hint was read (M3-33a).
          onFirstFlip: () => {
            options.onFirstFlip?.()
          },
          // A real pull, which is the only touch that proves the arrow was read (M3-51).
          onFirstPlunge: () => {
            options.onFirstPlunge?.()
          },
        })
        this.detachTouch = touch.detach
      } else {
        this.detachTouch = () => {}
      }

      // Resolved once. `browserMotionHost` answers `undefined` on a browser with no sensor API,
      // which is the same state as a player who declined — nothing attached, and the flick nudge
      // doing the whole job (08 §5.2).
      this.motionHost = options.motionHost === undefined ? browserMotionHost() : options.motionHost
      if (options.motionNudge === true) this.setMotionEnabled(true)

      const strip = options.dmdCanvas
      if (strip !== undefined && strip !== null) this.attachDmd(strip)

      // The gesture. A browser refuses to start an `AudioContext` outside one, and a context
      // created at load arrives `suspended` and stays that way. `once` on each, because either is
      // enough and the second would be a second context — two mixers into two limiters, summing.
      const start = (): void => {
        this.startAudio()
      }
      for (const type of ['keydown', 'pointerdown'] as const) {
        options.keyboard.addEventListener(type, start, { once: true })
      }
      this.detachGesture = () => {
        for (const type of ['keydown', 'pointerdown'] as const) {
          options.keyboard.removeEventListener(type, start)
        }
      }

      this.frameHandle = requestAnimationFrame(this.frame)
    } catch (error) {
      this.dispose()
      throw error
    }
  }

  /**
   * Turn this player's microphone off, or on again (M8-09).
   *
   * What the HUD control calls. The wrist menu reaches the same object through `onXrMenu`, so the
   * two controls are one state and cannot disagree — a HUD that said "on" over a wrist menu that
   * said "off" would be the worst version of this feature.
   */
  toggleMicrophoneMute(): void {
    this.voice.toggleMicrophone()
  }

  /** Turn everybody else's voices off, or on again (M8-09). 06 §5's "mute voice chat". */
  toggleVoiceMute(): void {
    this.voice.toggleOthers()
  }

  /**
   * What the browser said when the arcade asked for a microphone (M8-10).
   *
   * Part of `ArcadeHost`: `arcadePresence` reports it here on the way through, so the one object
   * that owns what the controls draw is also the one that hears whether there is a microphone
   * behind them. Called with `true` as well as `false` — a grant clears a notice a player
   * dismissed on an older, worse permission state.
   */
  reportMicrophone(granted: boolean): void {
    this.voice.setAccess(granted ? 'granted' : 'refused')
  }

  /** The player closed the notice about their refused microphone (M8-10). */
  dismissMicrophoneNotice(): void {
    this.voice.dismissMicrophoneNotice()
  }

  /**
   * This machine's own trace, for streaming to whoever is watching (M8-18).
   *
   * **The runtime does not know or care whether anybody is** — it describes what it is simulating
   * and the online game decides what to do with that. A runtime that had to be handed a game id
   * and a seat before it could answer would be a runtime that could not be built without a
   * backend, which is exactly what FR-030 forbids on the front door.
   *
   * `undefined` while nothing is being played. A machine in attract has a trace of no length, and
   * publishing it four times a second would tell three spectators to re-simulate nothing.
   */
  get localTrace(): LocalTrace | undefined {
    if (this.watching !== undefined) return undefined
    const game = this.session.game
    if (game.phase === 'attract' || game.isOver) return undefined
    const live = this.recorder.live
    const definition = this.session.loaded.definition
    return {
      machineId: definition.machineId,
      machineVersion: definition.machineVersion,
      coreVersion: CORE_VERSION,
      seed: this.options.seed ?? DEFAULT_SEED,
      playerCount: this.options.playerCount ?? 1,
      ballsPerGame: definition.meta.ballsPerGame,
      ball: game.currentBall,
      events: live.events,
      marks: live.marks,
      steps: Math.max(live.steps, game.step),
    }
  }

  /**
   * Play somebody else's ball on this machine (M8-19).
   *
   * **The spectator's own game is set aside rather than ended.** A player waiting for their turn
   * has a ball two sitting on this machine behind the ball one they have already played, and
   * watching the room's current ball — which is what standing at a machine in an arcade *is* —
   * must not cost them it. `endGhost` gives it back exactly as it was.
   *
   * Called four times a second with a trace that has grown; a change of `generation` means the
   * turn has passed and the machine is rebuilt for the new player's game rather than extended,
   * because two seats' traces are two different games with two different seeds (M8-18).
   *
   * **Verified as it plays.** The trace carries the player's own checkpoints, so a re-simulation
   * that disagrees says so instead of quietly showing a different ball — and a disagreement here
   * has caught *us* rather than anybody cheating, because both ends ran the same trace through the
   * same core (PH-088).
   */
  watchBall(view: { trace: ReplayTrace; liveSteps: number; generation: number }): void {
    const held = this.watching
    if (held === undefined || held.generation !== view.generation) {
      this.session.beginGhost(view.trace, { keepLive: true, verify: true })
      this.watching = { generation: view.generation, liveSteps: view.liveSteps }
      return
    }
    // A trace that refuses to extend is one that changed underneath — rebuilt rather than played
    // on, because playing on is the case that diverges with nothing to say why.
    if (!this.session.extendGhost(view.trace)) {
      this.session.beginGhost(view.trace, { keepLive: true, verify: true })
    }
    held.liveSteps = Math.max(held.liveSteps, view.liveSteps)
  }

  /** Give the machine back to this player's own game. Harmless when nothing is being watched. */
  stopWatching(): void {
    if (this.watching === undefined) return
    this.watching = undefined
    this.session.endGhost()
  }

  /** What this machine is showing of somebody else's ball, or nothing. */
  get spectating():
    | { readonly step: number; readonly behind: number; readonly divergedAt: number }
    | undefined {
    const live = this.watching
    if (live === undefined) return undefined
    const step = this.session.ghostProgress.step
    return {
      step,
      behind: Math.max(0, live.liveSteps - step),
      divergedAt: this.session.ghostDivergedAt,
    }
  }

  /** Is the sound up? False until a gesture has happened, and after a failure to start one. */
  get audible(): boolean {
    return this.audio !== null && this.audio !== undefined
  }

  /**
   * The player's audio preferences, and the only way to change them (07 §7).
   *
   * **Three writers — the HUD, the wrist menu and the settings page — and one owner**, the same
   * arrangement `VoiceMute` has and for the same reason. The mute used to be written straight to
   * the mixer from the wrist menu, which meant it was forgotten on every reload and invisible to
   * anything that was not the wrist menu.
   *
   * Every setter works whether or not there is a mixer yet. Before the first gesture there is no
   * `AudioContext` to write to, and a control that refused to answer until somebody had touched
   * the playfield would be off for exactly as long as the machine was silent anyway — so the
   * choice is stored, and `startAudio` builds the mixer from it.
   */
  get audioMuted(): boolean {
    return this.audioPrefs.muted
  }

  audioLevel(group: AudioGroup): AudioLevel {
    return this.audioPrefs.levels[group]
  }

  setAudioMuted(muted: boolean): void {
    if (this.audioPrefs.muted === muted) return
    this.audioPrefs = withAudioMuted(this.audioPrefs, muted)
    writeAudioMuted(muted)
    // Not `applyAudioPrefs`: the levels have not moved, and rewriting six bus gains to the values
    // they already hold would schedule six glides for nothing.
    this.audio?.mixer.setMuted(muted)
    // The wrist row states its condition, so it has to be told. Pushed rather than pulled, like
    // `voice`, because the menu redraws on its own frame and cannot reach back here.
    if (this.xr !== undefined) this.xr.audioMuted = muted
  }

  toggleAudioMuted(): void {
    this.setAudioMuted(!this.audioPrefs.muted)
  }

  /**
   * Move the master.
   *
   * **No getter beside it, deliberately.** One was written for symmetry with `audioMuted` — which
   * earns its place, because the readout hands it to the HUD's speaker button — and nothing
   * anywhere would have read this one. A member added for a caller that does not exist is what the
   * M7-46 scan found forty-five of.
   *
   * **The first caller `Mixer.setMasterVolume` has ever had.** It was written in M3-20 as a third
   * input to the master gain, correct and covered by its own test, and no code outside that test
   * called it — so 07 §7's "plus a master volume" and 06 §5's first Sound row were a requirement
   * with a working implementation and no way for a player to reach it.
   *
   * Not `applyAudioPrefs`: this is one gain node, and rewriting six bus volumes to the values they
   * already hold would schedule six glides for nothing. `setMuted` is left alone for the same
   * reason it leaves this alone — they are independent inputs to `Mixer.gainNow`, which is what
   * makes quieter-then-muted-then-unmuted land exactly where the player left it.
   */
  setAudioMaster(level: AudioLevel): void {
    if (this.audioPrefs.master === level) return
    this.audioPrefs = withAudioMaster(this.audioPrefs, level)
    writeAudioMaster(level)
    this.audio?.mixer.setMasterVolume(LEVEL_VOLUME[level])
    // The wrist row states its condition, so it has to be told — pushed, like the mute.
    if (this.xr !== undefined) this.xr.audioMaster = level
  }

  /** The next step of the master, wrapping. What one press of a wrist row does. */
  cycleAudioMaster(): void {
    this.setAudioMaster(nextAudioLevel(this.audioPrefs.master))
  }

  /**
   * Turn callout captions on or off, now rather than at the next mount.
   *
   * Live for the reason the mute is: the control is offered over a machine that is already
   * running, and a player who turns captions on mid-ball and sees nothing until they reload has
   * been handed a switch that looks broken at the moment they tried it.
   *
   * Not `applyAudioPrefs`: captions are not a bus, so no gain moves and nothing needs to glide.
   */
  setAudioCaptions(captions: boolean): void {
    if (this.audioPrefs.captions === captions) return
    this.audioPrefs = withAudioCaptions(this.audioPrefs, captions)
    writeAudioCaptions(captions)
    if (this.audio !== undefined) this.audio.captions = captions
    // The Sound page's captions row states its condition, so it is pushed like the other two.
    if (this.xr !== undefined) this.xr.audioCaptions = captions
  }

  /** Flip the captions. What one press of the wrist row does. */
  toggleAudioCaptions(): void {
    this.setAudioCaptions(!this.audioPrefs.captions)
  }

  setAudioLevel(group: AudioGroup, level: AudioLevel): void {
    if (this.audioPrefs.levels[group] === level) return
    this.audioPrefs = withAudioLevel(this.audioPrefs, group, level)
    writeAudioLevel(group, level)
    const mixer = this.audio?.mixer
    if (mixer !== undefined) applyAudioPrefs(mixer, this.audioPrefs)
  }

  /** The tier this is rendering at, and why. The settings panel reads it. */
  get tier(): MachineView['tier'] | undefined {
    return this.view?.tier
  }

  /** Is the phone's accelerometer nudging the machine right now? */
  get motionNudging(): boolean {
    return this.detachMotion !== undefined
  }

  /**
   * Turn device-motion nudging on or off, now rather than at the next mount.
   *
   * Live because the offer is made *over a machine that is already running* — a player who says
   * yes on their first ball and then has to reload before their phone does anything has been
   * given a control that appears broken at the exact moment they agreed to try it.
   *
   * The permission is the caller's to obtain: on iOS `requestPermission` must be called from
   * inside the click that answered the offer, and a runtime method is several frames too late
   * to be inside anything.
   */
  setMotionEnabled(on: boolean): void {
    if (on === this.motionNudging) return
    if (!on) {
      this.detachMotion?.()
      this.detachMotion = undefined
      return
    }
    const host = this.motionHost
    if (host === undefined) return
    this.detachMotion = attachMotionNudge(host, {
      controls: this.controls,
      // A stopped machine that still answers a shove is not stopped. The keyboard and the touch
      // layer do not need this — a paused machine has no ball for a flick to be aimed at, and
      // nobody flicks a screen they have deliberately frozen — but a phone in a moving hand
      // sends samples whatever the game is doing.
      enabled: () => !this.paused,
      onShove: () => this.options.onMotionShove?.(),
    }).detach
  }

  /** Put the camera on a preset. The phone layout asks for Follow. */
  setCamera(preset: CameraPreset): void {
    this.view?.setCamera(preset)
  }

  /**
   * Tap a flipper button, for an on-screen control rather than a thumb.
   *
   * **Through the controls, not into the game.** The initials a player enters end up on a
   * leaderboard row that a server re-simulates from the input trace, so they have to arrive by
   * the same route every other input does: a press this frame and a release the next, which is
   * what `PlaySession` edge-detects. Calling `Game.initialsNext()` directly would produce a
   * score whose initials no replay could reproduce.
   *
   * The release is unconditional, which would drop a bat a real finger was holding — acceptable
   * only because the buttons that call this exist for exactly one phase, high-score entry, where
   * there is no ball to flip at.
   */
  tapFlipper(side: 'left' | 'right'): void {
    if (side === 'left') this.controls.leftFlipper = true
    else this.controls.rightFlipper = true
    this.tapRelease.push(side)
  }

  /**
   * Decline the high-score table (M2-19a).
   *
   * **Through the controls, exactly like `tapFlipper` above and for the identical reason.** What
   * this does lands on a leaderboard row, so it has to arrive by the route a trace records; a
   * direct call to `Game.initialsSkip` would produce a row no replay could reproduce.
   */
  skipInitials(): void {
    // Recorded here rather than inferred later: this is the only moment anything knows the
    // difference between three characters committed and three characters abandoned.
    if (this.session.game.phase === 'highScoreEntry') this.skippedInitials = true
    this.controls.skipInitials = true
  }

  /**
   * Give up this game and leave the machine in attract (M8-80).
   *
   * **Not through the controls, and that is the difference from `skipInitials` above.** Declining
   * the table is something the *machine* does, so it goes through a latch and lands in the trace a
   * validator re-simulates. Abandoning is something the *player* does to the machine, it lands on
   * no leaderboard, and there is deliberately no core call for it — see `PlaySession.abandonGame`,
   * which replaces the game with the pristine simulation rather than ending it.
   *
   * The recorder is reset by the session, through `onNewGame`, so the abandoned inputs cannot
   * reach the next game's trace. **Nothing is saved on the way out**: the in-progress record is the
   * shell's to clear, because the shell is what offers a resume.
   */
  abandonGame(): void {
    this.session.abandonGame()
  }

  /** Pause or resume, for a button rather than a key. */
  togglePause(): void {
    this.controls.togglePause = true
  }

  /** Whether the simulation is stopped. The Builder's freeze button reads this. */
  get frozen(): boolean {
    return this.session.driver.isPaused
  }

  /** Set the next frame's pause state, including cancelling a pause not yet consumed. */
  setPaused(paused: boolean): void {
    this.controls.togglePause = this.frozen !== paused
  }

  /** Closing a reading/browsing surface never resumes a game. Resume is an explicit action. */
  openMachineGallery(picked: (id: string) => void): boolean {
    const current = this.session.loaded.definition.machineId
    const opened = this.xr?.showMachineGallery(current, (id) => {
      if (id === current) {
        // This card explicitly says Resume; keep the same simulation and recorded input path.
        this.xr?.menu.closeNormal()
        this.setPaused(false)
      } else picked(id)
    }, () => { this.xr?.openFullMenu() }) ?? false
    const phase = this.session.game.phase
    if (opened && !this.frozen && phase !== 'attract' && phase !== 'gameOver' && phase !== 'highScoreEntry') this.setPaused(true)
    return opened
  }

  /**
   * The simulation step the machine is on.
   *
   * Read rather than counted: a panel that counted its own presses would be a panel that disagrees
   * with the machine the moment one of them is refused, and the whole use of a single step is
   * trusting the number beside it.
   */
  get step(): number {
    return this.session.game.step
  }

  /**
   * Advance exactly one simulation step (M9-18).
   *
   * One **millisecond** of world: the simulation runs at a fixed 1000 Hz, so a step is the
   * smallest thing that happens on this machine. Answers whether it did, so a caller stepping
   * through a contact can tell a refusal from a step that changed nothing visible.
   */
  stepOnce(): boolean {
    if (!this.toolsAllowed || !this.session.driver.isPaused) return false
    // **One outstanding request at a time.** The flag is latched and the *frame loop* consumes it,
    // so twenty-five presses inside one frame are twenty-five writes of `true` and exactly one
    // step. Without this the caller is told twenty-five steps happened, which is how the panel
    // came to display "+25 ms" over a machine that had advanced one.
    if (this.controls.singleStep) return false
    this.controls.singleStep = true
    return true
  }

  /**
   * Record where the ball is on this step (M9-19).
   *
   * **Per step, which is per millisecond.** A trail sampled once a frame would be sixty points a
   * second and would draw a straight line through the whole of a contact: a ball meeting a
   * slingshot arrives, deforms and leaves inside about four milliseconds.
   *
   * **Whether this step touched something is asked of the contact feed's own counter.** The feed
   * knows which surface and how fast but not *where*; the position on the step it happened is
   * where, and this trail already holds that. One integer compare, and it cannot disagree with the
   * physics — which a second contact tap of our own could.
   */
  private markTrail(game: Game): void {
    const trail = this.trail
    if (trail === undefined) return
    const ball = game.world.activeBalls()[0]
    const seen = this.session.contacts.recorded
    const touched = seen > this.contactsSeen
    this.contactsSeen = seen
    if (ball === undefined) {
      // Between balls. Kept rather than cleared, so an author can look at how the last one drained
      // after it has gone — which is most of what a trajectory trace is for.
      this.trailBall = -1
      return
    }
    // A different ball is a different path. Without this the trail runs from where the last one
    // drained to where the next was served: a line straight up the playfield through the trough,
    // which is a path no ball ever took.
    const reset = trail.length > 0 && ball.prevX === ball.x && ball.prevY === ball.y && ball.prevZ === ball.z
      && (ball.x-this.trailX)**2+(ball.y-this.trailY)**2+(ball.z-this.trailZ)**2 > .016**2
    if (ball.id !== this.trailBall || reset) {
      trail.clear()
      this.trailBall = ball.id
    }
    trail.mark(ball.x, ball.y, game.step, touched, ball.z)
    this.trailX=ball.x; this.trailY=ball.y; this.trailZ=ball.z
  }

  /**
   * Start a mode, as a rule would (M9-22).
   *
   * *"Force state — Jump to any mode, multiball or wizard state to test its rules."*
   *
   * Answers `false` when the machine refused — a non-stackable mode will not start on top of
   * another — so the panel can say which mode is in the way rather than looking broken.
   */
  forceMode(id: string): boolean {
    if (!this.toolsAllowed) return false
    return this.session.game.engine.forceMode(id)
  }

  /** End a mode that is running. */
  endMode(id: string): boolean {
    if (!this.toolsAllowed) return false
    return this.session.game.engine.forceEndMode(id)
  }

  /**
   * How fast time runs, 0.1 to 1 (M9-23).
   *
   * **The simulation rate is unchanged.** A step is one millisecond of machine whatever this is;
   * what changes is how much wall clock a frame is allowed to spend, so at 0.2× a sixteen-
   * millisecond frame buys three steps instead of sixteen. Every one of those steps is exactly the
   * step it would have been at full speed, which is what makes slow motion worth looking at.
   */
  get timeScale(): number {
    const session = this.session
    if (session instanceof TestPlaySession) return session.timeScale_
    return this.buildTest ? this.buildTimeScale : FASTEST
  }

  set timeScale(scale: number) {
    if (!this.toolsAllowed) return
    const clamped = Math.min(FASTEST, Math.max(SLOWEST, scale))
    const session = this.session
    if (session instanceof TestPlaySession) {
      session.timeScale_ = clamped
    } else if (this.buildTest) {
      // The bench's session is the arcade's `PlaySession`, which has no speed of its own — by
      // design, so a scored game cannot be slowed. The draft's test slows the frame clock
      // instead, which is what `TestPlaySession.timeScale` does to the same driver.
      this.buildTimeScale = clamped
    }
  }

  /** Which modes are running. Read once a frame by the force-state panel. */
  get activeModes(): readonly string[] {
    return this.session.game.engine.activeModes()
  }

  /**
   * Is a ball off the playfield — up a ramp, in a habitrail, in the shooter lane (G2-11)?
   *
   * **Layer 0 is the playfield**, so anything else is a ball that has left it, which is what
   * "making a ramp" is. Read rather than derived from an event because there is no ramp *event*:
   * `EVENT_NAMES` has switches, targets, bumpers, slings and gates, and a ramp is a layer the ball
   * is on rather than a thing that happens to it.
   *
   * The shooter lane counts, and that is worth naming rather than excluding: it is a layer, a
   * player who has plunged has already got the first step, and a special case here would be this
   * module knowing which of a machine's layers the author considers impressive.
   */
  get ballOffPlayfield(): boolean {
    for (const ball of this.session.game.world.balls) {
      if (ball.active && ball.layer !== 0) return true
    }
    return false
  }

  /**
   * Everything "read machine aloud" describes, as one snapshot (G2-09, 06 §6).
   *
   * **Taken here rather than assembled by the caller**, because four of the six things 06 §6 names
   * are on the readout and two are not: *what is lit* and *the next objective* need the rule
   * engine's lamp map and the machine's own mode list, and neither has ever had a reader. A caller
   * reaching for them itself would be a second place that knows how a lamp code means "on".
   *
   * Built on demand rather than per frame. It is asked for by a person pressing something, at most
   * a few times a minute, and putting it on the readout would cost a walk of every lamp on every
   * frame for something nobody is listening to.
   */
  aloud(): AloudSnapshot {
    const game = this.session.game
    const engine = game.engine
    const definition = this.session.loaded.definition
    const active = engine.activeModes()
    const running = engine.runningMode()

    // Groups in the machine's own declaration order, counted rather than named one by one: a lamp
    // has an id, a colour and a group, and no human name — so the group is the closest thing to a
    // name the format guarantees, and it is also what a sighted player actually reads off a
    // playfield. See `machineAloud.ts`.
    const order: string[] = []
    const total = new Map<string, number>()
    const lit = new Map<string, number>()
    for (const lamp of definition.lamps ?? []) {
      const group = lamp.group
      if (group === undefined) continue
      if (!total.has(group)) {
        order.push(group)
        total.set(group, 0)
        lit.set(group, 0)
      }
      total.set(group, (total.get(group) ?? 0) + 1)
      // Anything but `LAMP_OFF` is lit. A blinking insert is lit — it is telling the player to
      // shoot there — and a report that called it dark would be describing the half-second it
      // happened to be sampled in.
      if (engine.lampOf(lamp.id) !== 0) lit.set(group, (lit.get(group) ?? 0) + 1)
    }

    // The mode's own timer, in whole seconds, from the machine's declared duration and the
    // fraction the engine reports. Whole seconds because this is *spoken*: "twenty-two point four
    // seconds left" is a measurement and "twenty-two seconds left" is an answer.
    const spec = definition.rules?.modes?.find((mode) => mode.id === running?.id)
    const timer = definition.rules?.timers?.find((t) => t.id === spec?.timer)
    const secondsLeft =
      running === undefined || timer === undefined
        ? 0
        : Math.round((running.remaining * timer.durationMs) / 1000)

    const player = game.players[game.currentPlayer]
    return {
      ball: game.currentBall,
      ballsPerGame: game.ballsPerGame,
      score: player?.score ?? 0,
      player: game.currentPlayer + 1,
      players: game.players.length,
      phase: game.phase,
      tilted: player?.tilted === true,
      mode: running === undefined ? undefined : { title: running.title, secondsLeft },
      lit: order.map((group) => ({
        group,
        lit: lit.get(group) ?? 0,
        total: total.get(group) ?? 0,
      })),
      objective: nextObjective(definition.rules?.modes ?? [], active),
      // The engine's own count, which the game writes on every step. `Game.ballsInPlay` is private
      // and this is the same number by construction — asking the public one avoids a second
      // opinion about what "in play" means.
      ballsInPlay: engine.ballsInPlay,
    }
  }

  /**
   * Move the ball to a point on the playfield, at rest (M9-18).
   *
   * *"Place ball — click anywhere to drop a ball there."*
   *
   * **The ball in play is moved, not a second one added** — that is `addBall`, a separate tool,
   * because a second ball has to be one the *game* serves (`Game.addBall`), not one the world
   * grows behind the game's back: a ball the rules did not count has a trough that disagrees with
   * the world, and the next drain ends a ball the game thinks is still out. With nothing in play
   * there is nothing to move and this says so.
   *
   * At rest, deliberately: an author placing a ball is asking "what happens from here", and a ball
   * that arrived carrying the velocity of wherever it used to be answers a different question.
   */
  placeBall(x: number, y: number): boolean {
    if (!this.toolsAllowed) return false
    const world = this.session.game.world
    const ball = world.activeBalls()[0]
    if (ball === undefined) return false
    ball.reset(x, y, ball.z)
    ball.active = true
    // **The trail starts again**, because a placed ball did not travel: a line from where it was to
    // where it was put is a path no ball took, and drawing one across the playfield is exactly the
    // sort of thing a trajectory trace exists to be trusted about.
    this.trail?.clear()
    return true
  }

  /**
   * Put one more ball on the playfield, at rest (26 Sep 2026: "a second ball in a test").
   *
   * The multiball an author wants to watch without first playing their way into it. **Through the
   * game, not the world**: `Game.addBall` takes a free slot and tells the rule engine the new
   * count exactly as a serve does, so a drain of either ball is an ordinary drain of one of two and
   * the last one down ends the ball — the trough never disagrees with the playfield. Test tools
   * only, the same gate as `placeBall`; false when the game is not in play or the machine has no
   * ball left to put out. The trail keeps drawing the ball it was drawing.
   */
  addBall(x: number, y: number): boolean {
    if (!this.toolsAllowed) return false
    return this.session.game.addBall(x, y)
  }

  /**
   * Put a ball in play, as the landing route's one primary control does.
   *
   * **Two machine actions, one press**, and the difference matters more than it looks. The launch
   * button on a real cabinet is three buttons wired to one — start the game, fire the plunger,
   * commit your initials — and which one it is depends on what the machine is doing. On the
   * landing route the machine is in attract, so the first press starts a game and leaves the ball
   * sitting in the shooter lane: a button labelled "▸ PULL THE PLUNGER" that does not pull the
   * plunger, and a visitor who is looking at an unchanged screen wondering whether it worked.
   *
   * So the press is held for the frame after, and spent again the moment there is something in
   * the lane to fire. The keyboard plunger is untouched by this — it is a key with two edges and
   * a draw length, and a player holding it is asking for a specific shot.
   *
   * Full power, because a button has no draw length. A player who wants the skill shot has a
   * plunger; a first-time visitor wants the ball in play.
   */
  launch(power = 1): void {
    this.controls.launchPower = power
    this.controls.launch = true
    // Three frames, not one: `startGame` may take a frame to seat the ball, and an unbounded
    // pending press would fire a plunger the player pressed ten seconds ago.
    this.pendingLaunch = { power, framesLeft: 3 }
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    cancelAnimationFrame(this.frameHandle)
    this.detachVoiceMute()
    // **Opened on the way out.** A machine disposed mid-callout would otherwise leave the gate
    // shut, and the player mute in a room they are still standing in — the arcade outlives this
    // object by four seconds every time a headset session ends (M8-06).
    this.speechGate.release()
    if (this.gateClosed) {
      this.gateClosed = false
      this.options.onVoiceGate?.(false)
    }
    this.detachGesture()
    this.detachKeyboard()
    this.detachTouch()
    this.detachMotion?.()
    this.detachMotion = undefined
    this.dmd?.dispose()
    this.dmd = undefined
    // The session, before the renderer it is presenting through. A renderer disposed underneath a
    // live session leaves the runtime holding a dead GL context, which on Quest Browser is the
    // one thing that does not recover.
    this.xrSources.close()
    this.ambience?.dispose()
    this.ambience = undefined
    this.view?.host.renderer.setAnimationLoop(null)
    this.xr?.dispose()
    this.xr = undefined
    this.view?.dispose()
    this.view = undefined
    this.audio?.dispose()
    this.audio = undefined
    // A motor that was mid-pulse when the route unmounted keeps buzzing on its own: `vibrate` is
    // fire-and-forget and nothing cancels it when the page that asked goes away.
    this.haptics.stop()
    // **Closed, not merely disconnected.** A browser allows about half a dozen live
    // `AudioContext`s per page and then refuses; each route mount builds one, so leaving them
    // open makes the sixth navigation the one where the machine goes permanently silent — six
    // clicks away from a bug nobody would reproduce on the way to reporting it. The next mount
    // opens a fresh one on its own first gesture, which it is going to get anyway.
    void this.audioContext?.close().catch(() => {
      // Already closed, or closing twice under a double-invoked effect. Nothing to do about it
      // and nothing worth telling the player.
    })
    this.audioContext = undefined
  }

  private startAudio(): MachineAudio | undefined {
    if (this.audio !== undefined || this.audioTried) return this.audio
    this.audioTried = true
    try {
      const Ctor = (this.options.keyboard as { AudioContext?: typeof AudioContext }).AudioContext
      if (Ctor === undefined) throw new Error('this browser has no Web Audio')
      const context = new Ctor()
      this.audioContext = context
      this.audio = new MachineAudio(context as unknown as AudioPlaybackContext, this.session.loaded, {
        tier: renderTier().tier,
        now: () => performance.now(),
        audio: this.audioPrefs,
      })
      void context.resume()
    } catch {
      // A machine with no sound is a machine you can still play. Left undefined; `audible` says
      // so, and the HUD's mute control renders disabled rather than lying about being on.
      this.audio = undefined
    }
    return this.audio
  }

  /** Can this device present this mode? A button reads it to decide what it says. */
  async supportsXr(mode: ImmersiveMode): Promise<boolean> {
    return (await this.xr?.supports(mode)) ?? false
  }

  /** What the last XR frame reported — the reticle, the toasts, the menu. */
  get xrFrameReport(): XRFrameReport | undefined {
    return this.xrReport
  }

  /**
   * Put the other people in the arcade, or take them out again (M8-04, M8-05).
   *
   * A setter rather than a constructor option because **this module may not build one**: a
   * `VenuePort` comes from the account chunk, which is behind the lazy boundary FR-030 depends on,
   * and `machineRuntime.ts` is on the front door. The shell fetches that chunk when a player
   * enters a headset and hands the result down.
   */
  setVenuePresence(port: VenuePort | undefined): void {
    if (this.xr !== undefined) this.xr.venuePresence = port
  }

  get presentingXr(): boolean {
    return this.xr?.presenting === true
  }

  /**
   * Enter a headset.
   *
   * The frame loop **moves**: WebXR frames come from the session, not from the browser, and a
   * page that kept calling `requestAnimationFrame` while presenting would render the flat view
   * into a canvas nobody is looking at and never draw a headset frame at all. The simulation is
   * untouched by the swap — it is advanced from the frame callback either way, by elapsed time,
   * and `SimulationDriver` has never cared where the callback came from.
   */
  async enterXr(mode: ImmersiveMode): Promise<boolean> {
    const xr = this.xr
    const view = this.view
    if (xr === undefined || view === undefined) return false
    // **VR is a room inside the passthrough session** (15 Sep 2026, the owner's decision). Where
    // the headset has passthrough, Enter VR starts the same `immersive-ar` session Enter MR does
    // and asks the runtime for the arcade look; the wrist can then switch looks in place, both
    // ways, and nothing has to end (`XRRuntime.setLook`). A headset without passthrough gets the
    // plain `immersive-vr` session it always got.
    const look: Look = mode === 'immersive-vr' ? 'vr' : 'mr'
    const session: ImmersiveMode = look === 'vr' && (await xr.supports('immersive-ar')) ? 'immersive-ar' : mode
    if (!(await xr.enter(session, look))) return false

    const xrSession = view.host.renderer.xr.getSession()
    await this.xrSources.open(xrSession, session)

    cancelAnimationFrame(this.frameHandle)
    this.frameHandle = 0
    view.host.renderer.setAnimationLoop((now: number, xrFrame?: unknown) => {
      this.frame(now, xrFrame)
    })
    return true
  }

  async exitXr(): Promise<void> {
    await this.xr?.exit()
  }

  /**
   * The session ended — by request, or because the runtime took it away.
   *
   * Puts the browser's frame loop back and hands the machine its ordinary place in the scene.
   * Called for a deliberate exit and for an involuntary one alike, because the cleanup is the
   * same and the *difference* is a sentence the shell says, not a different teardown.
   */
  private onXrEnded(): void {
    this.xrSources.close()
    this.ambience?.dispose()
    this.ambience = undefined
    this.xrReport = undefined
    const view = this.view
    if (view === undefined || this.disposed) return
    view.host.renderer.setAnimationLoop(null)
    view.scene.root.position.set(0, 0, 0)
    view.scene.root.rotation.set(0, 0, 0)
    view.scene.root.scale.setScalar(1)
    this.lastFrame = -1
    this.frameHandle = requestAnimationFrame(this.frame)
  }

  /**
   * Read the headset, and turn what the player's hands did into controls.
   *
   * Before the session's frame, because the controls it writes are the controls that frame will
   * read — a headset flip applied afterwards would land one frame late, which is 14 ms at 72 Hz
   * and is exactly the budget XR-032 is trying to protect.
   *
   * Does nothing at all on a flat frame, which is every frame outside a session.
   */
  private advanceXr(xrFrame: unknown, frameMs: number): void {
    const xr = this.xr
    const view = this.view
    // **Every one of these returns is a frame with no head in it**, which is what a flat screen
    // is — and a listener left standing from the last immersive frame would keep spatialising a
    // machine the player is now looking at on a monitor (M9-48). Cleared first, once, rather
    // than at each `return`: this method grew four of them and the fifth would forget.
    if (xr === undefined || view === undefined || xrFrame === undefined) {
      this.audio?.listenAt(undefined)
      return
    }
    const mode = xr.manager.mode
    const referenceSpace = view.host.renderer.xr.getReferenceSpace()
    const session = view.host.renderer.xr.getSession()
    if (mode === undefined || referenceSpace === null || session === null) {
      this.audio?.listenAt(undefined)
      return
    }

    const input = frameInput(xrFrame, referenceSpace, session, this.xrSources, mode)
    // **NFR-022's second half, handed over before the frame that draws it** (M9-47). The caption
    // reaches `MachineStage`'s DOM, which is not composited in an immersive session — so a player
    // in a headset was getting the "on screen" half of *"captioned on screen and in the headset"*
    // and none of the other. The audio layer owns how long a caption lasts, so this is a value
    // that arrives and leaves rather than an event the XR layer has to hold; `audioMuted` and
    // `audioMaster` are handed over the same way and for the same reason.
    xr.caption = this.audio?.caption
    const report = xr.advance(input, frameMs / 1000, this.controls)
    this.xrReport = report
    // **07 §8's other half, four milestones late** (M9-48). `space.ts` has said since M3-30 that
    // its pan and its level are "the seam M5 replaces" — in a headset the same placement was to
    // drive an HRTF panner against a listener on the camera. M5 shipped, and so did M6, M7 and
    // M8, and every sound in the browser went through a `StereoPannerNode`: the knocker in the
    // cabinet head, the speech out of the backbox and a bumper at the far end of the playfield
    // were all the same distance away, at the same height, in the middle of the player's skull.
    //
    // **After `advance`, because `advance` is what may have moved the machine** — a recentre, a
    // carry, a scale change — and a listener posed against last frame's placement is a machine
    // that is heard where it used to be.
    this.audio?.listenAt(headInMachine(view, input.head))
    // **Making the machine belong to the room it is standing in** (M9-51, M5-19, 06 §3).
    // `lightingMatchFor` has turned the runtime's light estimate into three instructions on every
    // frame since M5-19, `XRFrameReport.lighting` has carried them, and a grep for a reader
    // returned the field's declaration and the line that assigns it. A cabinet lit for a neon
    // arcade, standing in a warm living room, read as a sticker — which is the sentence
    // `lightingMatch.ts`'s own header uses.
    //
    // **Only in the passthrough look.** The estimate describes a *real* room, and the VR arcade
    // is a room this product already lights: tinting its machines by a light probe of a room
    // nobody is looking at, and pooling light on a carpet that has its own, would be a second
    // opinion about both. `undefined` is the machine's own colours and no pool. By the look, not
    // the session (15 Sep 2026): the arcade can be up inside a passthrough session.
    view.scene.matchRoom(report.look === 'mr' && report.presenting ? report.lighting : undefined)
    // **Only while a ball is in play** (M9-38). The minutes spent placing the machine, and the
    // minutes after a game over with the headset still on, say nothing about how the game was
    // played — and a player who takes the headset off between balls has not switched to a
    // keyboard.
    if (report.presenting && this.session?.game.phase === 'ballInPlay') {
      if (report.method === 'xr-hands') this.xrFrames.hands += 1
      else this.xrFrames.controllers += 1
    }
    this.advanceAmbience(report.look, frameMs)
  }

  /**
   * A wrist-menu choice the headset could not perform on its own (M5-24).
   *
   * `XRRuntime` handles the three that are its own business — leaving XR, comfort mode and the
   * scale — and hands the rest here, because pausing a game and muting a mixer are this object's
   * business and giving the XR layer a reference to either would be a second owner of both.
   */
  private onXrMenu(selection: MenuSelection): void {
    switch (selection.item) {
      case 'pauseResume':
        // The same latch the pause button and the P key use, so a headset pause and a keyboard
        // pause are one code path and one recorded action.
        this.togglePause()
        return
      case 'muteAll':
        // **Through the same latch the HUD button uses**, so the wrist row and the flat-screen
        // button cannot disagree, and so a mute made in VR is still a mute after the headset has
        // been off long enough for the page to reload.
        //
        // Was `case 'sound'` until 06 §5's Sound page landed. That row pages now, and a page
        // change never reaches this method — `WristMenu.select` returns nothing for one, so a
        // stale case here would simply have stopped muting anything.
        this.toggleAudioMuted()
        return
      case 'masterVolume':
        // Cycles rather than sets, because a wrist row is one button and the three steps have to
        // fit through it. The settings page, which has room for three, sets them directly — both
        // through the same latch, so neither surface can be showing a level the mixer is not at.
        this.cycleAudioMaster()
        return
      case 'captions':
        this.toggleAudioCaptions()
        return
      case 'muteMic':
        // **The state is flipped here and applied elsewhere** (M8-09). `VoiceMute` tells the XR
        // runtime — which is what redraws the row the player is still looking at — and the shell,
        // which is the only thing on this side of the lazy boundary that can reach a microphone.
        this.voice.toggleMicrophone()
        return
      case 'muteVoice':
        this.voice.toggleOthers()
        return
      case 'skipInitials':
        // **Down, not up** (M2-19a), unlike `endGame` below. Declining the table is something the
        // *machine* does and the core already has a call for it, so it goes through the same latch
        // the flat screen's Skip button uses — one code path, one recorded action, one trace a
        // validator can re-simulate. Ending a game has no such call and must not grow one, which
        // is the distinction the default case argues.
        this.skipInitials()
        return
      default:
        // **`endGame` and `machine` go up, not down, and deliberately.** The core has no
        // "abandon this game" call and must not grow one: a game that ended by a route the
        // validator has never seen is a score nobody can check. Ending a game is therefore a
        // *shell* action — unmount the machine, keep or discard what it wrote — and navigating to
        // another machine plainly is. Forwarded rather than swallowed, so a menu row that a
        // player pressed always does something.
        this.options.onXrMenu?.(selection)
        return
    }
  }

  /**
   * Build mode begins: remember the machine as it is, so it can be put back (M13-10).
   *
   * Nothing is rebuilt here — the draft opens *on* the running machine's definition, so the
   * first commit is the first rebuild. Idempotent: a second call while the mode is on keeps the
   * original it already holds rather than overwriting it with a draft.
   */
  private enterBuild(): void {
    if (this.buildOriginal !== undefined) return
    this.buildOriginal = this.options.machine
  }

  /**
   * The draft changed: the session's machine becomes it, and the picture follows (M13-10).
   *
   * `rebuild` is a fresh construction — the same `reset` the harness uses — and `patch` then
   * redraws only the devices whose definition object changed and rebinds the rest to the new
   * world. A rebuilt session is in attract, so the author's next act is the start button, as it
   * is for a player.
   */
  private adoptDraft(definition: MachineDefinition): void {
    this.session.rebuild(definition)
    this.view?.scene.patch()
    this.afterRebuild()
  }

  /** A draft is under Test play on the bench (M13-25): the tools are allowed and the trail records. */
  private beginBuildTest(): void {
    this.buildTest = true
    this.buildTimeScale = 1
    this.ribbonOn = true
    if (this.trailNow === undefined) this.trailNow = new BallTrail()
    this.trailNow.clear()
  }

  /** The ribbon's key, in the player's language (the test-tool follow-up, 25 Sep 2026). */
  private ribbonLegendNow(): readonly RibbonLegendRow[] {
    const language = readLanguage()
    if (this.ribbonKey === undefined || this.ribbonKey.language !== language) {
      this.ribbonKey = { language, rows: ribbonLegend(benchStringsFor(language)) }
    }
    return this.ribbonKey.rows
  }

  /** Back to build: the tools are refused again, the clock runs at full speed, the ribbon comes down. */
  private endBuildTest(): void {
    if (!this.buildTest) return
    this.buildTest = false
    this.buildTimeScale = 1
    this.ribbonOn = false
    if (this.session.driver.isPaused) this.controls.togglePause = true
    if (this.options.testTools !== true) this.trailNow = undefined
    this.view?.scene.ribbon(undefined)
  }

  /** Build mode ends: the machine the session was built with, back, and the picture with it. */
  private leaveBuild(): void {
    const original = this.buildOriginal
    if (original === undefined) return
    this.buildOriginal = undefined
    this.session.rebuild(original)
    this.view?.scene.patch()
    this.afterRebuild()
  }

  /**
   * What a rebuilt session leaves stale in this object.
   *
   * The `TraceRecorder` is not among them: it captured the machine's id and version at
   * construction and the draft keeps both, and nothing it records while a draft runs is ever
   * read (`remember` refuses). The driver is reset by `PlaySession.reset` itself. `wasOver` is
   * stale: a draft game left at game over would otherwise be "already written down" on the
   * frame the original machine came back, and the next real game over would go unrecorded.
   */
  private afterRebuild(): void {
    this.wasOver = false
  }

  /**
   * Shut the outgoing microphone while this machine talks, and open it afterwards (M8-13).
   *
   * **The audio layer is asked rather than the session**, because `session.spoke` is the frame a
   * line *started* on and a line takes about a second to say. `MachineAudio.speaking` is the same
   * clock the caption comes down on and the speech duck comes off, so all three agree about when
   * a callout ends instead of each keeping its own timer.
   *
   * Reported only on a change. This runs at 72–120 Hz and the answer changes a couple of times a
   * ball; a callback per frame would be a dynamic import's promise per frame.
   *
   * **And only while the callouts are actually audible** (07 §7). The gate exists so the machine's
   * voice does not leave through the player's microphone; with the callout bus muted nothing comes
   * out of their speakers, so there is nothing to bleed and cutting them off mid-sentence is pure
   * cost. `speaking` cannot answer this on its own — it is the caption's clock, and the caption is
   * still shown when the sound is off, which is exactly what NFR-022 asks for.
   */
  private advanceVoiceGate(frameMs: number): void {
    const aloud =
      this.audio?.speaking === true && !this.audioPrefs.muted && this.audioPrefs.levels.speech !== 'off'
    const closed = this.speechGate.advance(aloud, frameMs / 1000)
    if (closed === this.gateClosed) return
    this.gateClosed = closed
    this.options.onVoiceGate?.(closed)
  }

  /** The venue's room tone, ducked under the machine's own speech. By the look, not the session. */
  private advanceAmbience(look: Look | undefined, frameMs: number): void {
    const audio = this.audio
    if (audio === undefined) return
    if (look !== 'vr') {
      this.ambience?.advance(frameMs / 1000, false, false)
      return
    }
    this.ambience ??= new ArcadeAmbience(audio.context, audio.mixer.bus('ambience').input)
    // `spoke` is this frame's callouts. A line takes longer than a frame to say, so the duck is
    // held by its own release constant rather than by this being true continuously.
    // **The row before the frame** (M8-21): who is playing which machine decides what the room
    // sounds like, and a bed levelled from last frame's occupancy would lag a teleport by a frame.
    // Cheap to ask every frame — `roomSounds` recomputes only when somebody moves.
    this.ambience.setRoom(this.xr?.roomSounds ?? [])
    this.ambience.advance(frameMs / 1000, this.session.spoke.length > 0)
  }

  private readonly frame = (now: number, xrFrame?: unknown): void => {
    if (this.disposed) return
    // Presenting? The session is driving, and asking the browser for a frame as well would run
    // two loops into one simulation.
    if (!this.presentingXr) this.frameHandle = requestAnimationFrame(this.frame)

    const frameMs = this.lastFrame < 0 ? 0 : now - this.lastFrame
    this.lastFrame = now

    this.advanceXr(xrFrame, frameMs)

    const ghost = this.options.ghost
    const plunger = this.controls.chargeStartedAt
    let state: SessionFrame
    const live = this.watching
    if (live !== undefined) {
      // **The pace is decided from two step counts and nothing else** — see `spectatorPacing`. The
      // brake is `ghostFrame`'s own clamp to the trace it has, so all that is written here is the
      // accelerator and the skip.
      const plan = spectatorPlan(this.session.ghostProgress.step, live.liveSteps)
      if (plan.kind === 'seek') this.session.seekGhost(plan.to)
      state = this.session.ghostFrame(frameMs / 1000, plan.speed)
    } else if (ghost !== undefined) {
      // A scrub first, so a drag lands before the frame it was dragged on rather than a frame
      // later — sixteen milliseconds is invisible on a click and very visible on a scrub bar.
      const seek = ghost.takeSeek()
      if (seek !== undefined) this.session.seekGhost(seek)
      state = this.session.ghostFrame(frameMs / 1000, ghost.speed())
      const progress = this.session.ghostProgress
      ghost.onProgress?.(progress.step, progress.steps)
    } else {
      // A draft under test on the bench runs at the test tools' speed (M13-25): the same steps,
      // fewer of them per frame.
      state = this.session.frame((frameMs / 1000) * (this.buildTest ? this.buildTimeScale : 1), this.controls)
      // A test game that has ended is served again (the 1.0.108 round, `wantsANewGame`).
      if (this.buildTest && wantsANewGame(this.session.game.phase)) {
        this.session.startGame(0)
        this.trailNow?.clear()
      }
    }
    // The ribbon (M13-25): the trail as it is now, while the bench asks for it — and its key.
    this.view?.scene.ribbon(this.ribbonOn ? this.trailNow : undefined, this.ribbonOn ? this.ribbonLegendNow() : undefined)
    this.paused = state.paused
    // **A paused machine goes quiet** (M3-55). The simulation stopping is not enough on its own:
    // the rolling bed is a loop, the music sequencer runs off the audio clock and neither is
    // driven by `Game.advance`, so a pause without this leaves a stopped machine still humming.
    // Told every frame rather than on the edge, because `setPaused` is idempotent and a runtime
    // that keeps its own copy of the audio's state is a second thing that can be wrong.
    this.audio?.setPaused(state.paused)
    // **The wrist menu's Pause row was reading the wrong pause** (M3-55). `MenuState.paused` came
    // from `pausedByVisibility`, which is set when the *headset's system menu* opens — a moment
    // when the wrist panel cannot be seen at all. So the row said "Pause" over a machine the player
    // had just paused from that very row, and read "Resume" essentially never.
    if (this.xr !== undefined) this.xr.gamePaused = state.paused
    // The row that declines the high-score table exists only while there is one to decline
    // (M2-19a). Read off the game rather than mirrored, for the reason the pause above records:
    // a second copy of a state is a second thing that can be wrong about it.
    if (this.xr !== undefined) {
      this.xr.enteringInitials = this.session.game.phase === 'highScoreEntry'
      this.xr.gameInProgress = this.session.game.phase !== 'attract' && this.session.game.phase !== 'gameOver' && !this.xr.enteringInitials
    }
    // After the frame that saw the press, so the session gets a clean down-then-up edge.
    for (const side of this.tapRelease) {
      if (side === 'left') this.controls.leftFlipper = false
      else this.controls.rightFlipper = false
    }
    this.tapRelease.length = 0

    if (this.view !== undefined) {
      this.view.resize()
      this.view.render(state.alpha, frameMs)
      const stepped = this.view.takeNotice()
      if (stepped !== undefined) this.sayOnce(stepped, now)
    }

    // **The recording has stopped, and until today nothing said so.** `TraceRecorder.capped` has
    // carried the doc line "the settings page shows it; a test asserts on it" since it was
    // written, and it had **no reader in either client** — the test half was true and the settings
    // half was not. Past `MAX_TRACE_STEPS` the recorder keeps no further actions, so the game can
    // no longer be posted (`traceProblem` refuses it, and `Replay` with it) and can no longer be
    // resumed (`resumable` refuses it). A player two hours in was losing both in silence.
    if (this.recorder.capped && !this.saidCapped) {
      this.saidCapped = true
      this.sayOnce(RECORDING_CAPPED, now)
    }

    // A one-off is an **event**, so its line expires. Left standing it becomes furniture: a
    // sentence about a graphics change that happened four minutes ago, parked over the playfield
    // for the rest of the session, still there when the machine is running perfectly.
    if (this.oneOff !== '' && now > this.oneOffUntil) this.oneOff = ''

    // The DMD strip, from the same raster the backglass shows. After the session's frame, so the
    // dots are this frame's dots.
    if (this.dmd !== undefined && this.dmdContext !== undefined) {
      const raster = this.session.display.player.raster
      // The drawing buffer follows the element, and the element follows the layout. Asking every
      // frame is a property read; being told would need a `ResizeObserver` per strip.
      const strip = this.dmdContext.canvas as HTMLCanvasElement
      const wide = Math.max(1, Math.round(strip.clientWidth * dpr()))
      const tall = Math.max(1, Math.round(strip.clientHeight * dpr()))
      if (strip.width !== wide || strip.height !== tall) {
        strip.width = wide
        strip.height = tall
      }
      this.dmd.paint(raster)
    }

    const audio = this.audio
    if (audio !== undefined) {
      // After the simulation, so this frame's events are this frame's sounds, and outside the
      // step callback because sixteen steps of events arrive together and no ear resolves
      // sixteen milliseconds.
      audio.update(this.session.loaded.world, this.session.events, this.session.contacts)
      for (const x of this.session.drained) audio.drain(x)
      for (const id of this.session.spoke) audio.speak(id)
      for (const id of this.session.sounded) audio.sound(id)
      audio.music(this.session.musicState)
      audio.mixdown(this.session.mixState)
      // The plunger and the tilt bob are the player's half of the machine and neither is a
      // physical event the solver produces. Gated on `awaitingLaunch`, because the same control
      // starts a game and enters initials — and a plunger twang with no plunger behind it is a
      // machine describing something that did not happen.
      const game = this.session.game
      audio.cabinet(game.tilt, game.canManualLaunch ? this.plungerDraw(now, plunger) : 0)
    }

    // The same frame, to the hand (M3-42).
    //
    // **Reading the contact feed rather than the audio layer's opinion of it.** A haptic is not a
    // quieter sound: the vocabulary is four pulses a motor can express, where the mixer has a
    // hundred voices, and routing one through the other would tie the buzz to whether a sound was
    // audible, muted, or culled by the voice pool. It is also why this works with the sound off.
    //
    // **The strongest contact of the frame, not each one.** A frame can carry hundreds and the
    // hand can feel one; `TouchHaptics` has the rate floor, and taking the maximum here is what
    // makes the buzz mean "the biggest thing that just happened".
    if (this.haptics.on) {
      let hardest = 0
      this.hapticCursor = this.session.contacts.read(this.hapticCursor, (_ball, _surface, speed) => {
        if (speed > hardest) hardest = speed
      })

      const game = this.session.game
      // **The rising edge of a bat, from whatever pressed it.** Read off the controls rather than
      // from the touch listener, so a keyboard and a headset trigger get the same click — and so
      // there is one place this is decided rather than one per input device.
      const leftEdge = this.controls.leftFlipper && !this.flipperWas.left
      const rightEdge = this.controls.rightFlipper && !this.flipperWas.right
      this.flipperWas = { left: this.controls.leftFlipper, right: this.controls.rightFlipper }

      // The plunger letting go: the lane emptying is the edge, because `launch` is consumed by
      // the simulation before this runs.
      const launched = this.awaitingWas && !game.awaitingLaunch

      // Order matters, because the rate floor means only one of these lands, and the order is the
      // order a player cares about them. The machine refusing outranks everything; a ball being
      // lost is next; a bat under the thumb is the feedback a glass control cannot otherwise
      // give, so it outranks the ball hitting something.
      const tilted = game.tilt.tilted === true
      if (tilted && !this.wasTilted) this.haptics.pulse('tilt')
      else if (this.session.drained.length > this.drainedSeen) this.haptics.pulse('drain')
      else if (launched) this.haptics.pulse('launch')
      else if (leftEdge || rightEdge) this.haptics.pulse('flipper')
      else if (hardest > 0) this.haptics.contact(hardest)

      this.wasTilted = tilted
      this.drainedSeen = this.session.drained.length
      this.awaitingWas = game.awaitingLaunch
    } else {
      // Keep the cursor and the edges level with the machine while silent, or turning haptics on
      // mid-game would replay every contact since it was built as one enormous buzz.
      this.hapticCursor = this.session.contacts.cursor
      this.flipperWas = { left: this.controls.leftFlipper, right: this.controls.rightFlipper }
      this.drainedSeen = this.session.drained.length
      this.awaitingWas = this.session.game.awaitingLaunch
    }

    // 07 §7's outbound voice gate (M8-13). **After `audio.speak`**, so a callout that started on
    // this frame shuts the microphone on this frame rather than on the next one — sixteen
    // milliseconds is nothing to an ear and it is the whole first consonant to an encoder.
    // Advanced even with no audio at all, so a gate that was shut when the context died opens.
    this.advanceVoiceGate(frameMs)

    this.spendPendingLaunch()
    this.remember(now)
    this.reportControls(now, plunger)
    this.publish(now, plunger)
  }

  /**
   * Write the game down (M3-39).
   *
   * Two rhythms, because they answer different questions. The **interrupted game** is saved on a
   * wall clock, because what it protects against is the tab going away — a crash, a phone call, a
   * browser reclaiming memory — and none of those happen on a simulation step. The **finished
   * game** is saved on the edge where `isOver` becomes true, because that is a fact about the
   * game and happens exactly once.
   *
   * Both writes are fire-and-forget. IndexedDB is asynchronous and the frame loop is not: an
   * `await` here would be a machine that stutters when it saves.
   */
  private remember(now: number): void {
    // **Nothing about somebody else's ball is written down here** (M8-19). While this machine is
    // showing a spectated game, `session.game` is that game — and saving it would put another
    // player's score on this browser's local table and offer their ball as an interrupted game to
    // resume. The player's own game is set aside, untouched, and comes back with `endGhost`.
    if (this.watching !== undefined) return
    // **Nor anything about a draft** (M13-10). A Build-mode test is 11 §4's "never in a scored
    // game": the machine under the ball is one nobody has published, its trace would name a
    // machine version the validator cannot load, and a score on the local table would be a score
    // for a machine that may not exist tomorrow. The same one line as the ghost, for the same
    // reason: it is not this player's game on this machine.
    if (this.buildOriginal !== undefined) return
    const history = this.options.history?.()
    if (history === undefined) return
    const game = this.session.game

    // The initials as they are typed. Captured here rather than read at the end, because by then
    // the machine has moved on to the next qualifying player and reset them.
    if (game.phase === 'highScoreEntry') this.typedInitials = game.initialsText

    const over = game.isOver
    if (over && !this.wasOver) {
      this.wasOver = true
      const trace = this.recorder.trace(game)
      const at = Date.now()
      const inputMethod = this.inputMethodPlayed()

      // **Queued first, and the rest chained behind it.** IndexedDB has no transaction across
      // these writes, so the order decides what a crash between any two of them leaves behind.
      // Queue first, and an interruption leaves a game the player is still offered a resume of:
      // they finish it again, produce the same trace, and `submissionKey` gives it the same key,
      // so it is one submission. Score first, and an interruption leaves the score on the local
      // table with nothing queued to post it — which nothing later can repair, because the game
      // it came from has already been marked finished.
      // **Remembered only when it was committed** (M2-19a). A skip is a decision not to be
      // named, so it leaves the memory exactly as it was; and a game that never reached the table
      // has nothing to remember. See `writeLastInitials`.
      if (!this.skippedInitials && this.typedInitials !== '') {
        writeLastInitials(this.typedInitials)
      }

      const queue = this.options.outbox?.()
      const queued = queue === undefined ? Promise.resolve(false) : queue.enqueue(trace, inputMethod, at)
      void queued.then(() => {
        void history.addReplay(trace, at)
        void history.clearInProgress(trace.machineId)
        void history
          .addScore({
            machineId: trace.machineId,
            initials: finalInitials(this.typedInitials, this.skippedInitials),
            score: trace.claimedScore,
            at,
            inputMethod,
          })
          .then((made) => this.options.onSaved?.(trace.claimedScore, made))
      })
      return
    }
    // A new game on the same machine used to be noticed here, one frame after it began, and the
    // recorder reset then — which threw away the press that began it. The session now says so
    // itself, before that press is recorded: see `onNewGame`.
    if (over || game.phase === 'attract') return
    if (now - this.savedAt < RESUME_SAVE_MS) return
    this.savedAt = now
    // The ball comes from the game, not from the trace. See `saveInProgress`: a trace records
    // controls and a ball number is a consequence, so this is the only place that knows it.
    void history.saveInProgress(this.recorder.trace(game), game.currentBall)
  }

  /** Fire a held button press once the machine has a ball in the lane for it. */
  private spendPendingLaunch(): void {
    const pending = this.pendingLaunch
    if (pending === undefined) return
    if (this.session.game.canManualLaunch) {
      this.controls.launchPower = pending.power
      this.controls.launch = true
      this.pendingLaunch = undefined
      return
    }
    pending.framesLeft--
    if (pending.framesLeft <= 0) this.pendingLaunch = undefined
  }

  /**
   * How far the plunger is drawn, 0…1, from whichever control is drawing it.
   *
   * Two answers to one question, because the two controls measure different things: a key is
   * timed, and a drag is a distance. The touch value wins when there is one — a player with a
   * finger on the arc is not also holding the space bar, and if they somehow are, the finger is
   * the one they are looking at.
   */
  /**
   * Hand the drawn cabinet controls their state, on a change (M3-49).
   *
   * **A separate channel from `publish` on purpose.** A bat goes up and down several times a
   * second and the plunger knob follows a thumb continuously, and both of those would otherwise be
   * re-renders of the whole stage — see `onControls`. The draw is compared at half a per cent,
   * which is under a pixel of knob travel on any screen this runs on, so holding the plunger still
   * costs nothing.
   */
  /**
   * Is the plunger a control the player can use right now (M3-51)?
   *
   * **One predicate, read by both the listener and the drawing.** They were briefly two — the
   * touch gate learned about attract and `reportControls` did not — which is the exact defect
   * `phone.ts` exists to prevent, arrived at from the other direction: a plunger a thumb could pull
   * and the screen drew as dead, with the arrow teaching the gesture invisible because it is gated
   * on the drawn state. Found by the end-to-end test rather than by reading either half.
   *
   * Attract counts, because there the launch control is the *start* button and pulling it is how a
   * phone player begins. A ball actually in play does not: a drag in the bottom-right corner
   * mid-ball is somebody reaching for a flipper, not asking for another ball.
   */
  private get plungerLive(): boolean {
    const game = this.session.game
    return game.canManualLaunch || game.phase === 'attract'
  }

  private reportControls(now: number, plunger: number | null): void {
    const report = this.options.onControls
    if (report === undefined) return
    const live = this.plungerLive
    const next: ControlState = {
      left: this.controls.leftFlipper,
      right: this.controls.rightFlipper,
      // Zero unless there is something to fire, so a knob cannot be shown drawn against an empty
      // lane — the same gate `audio.cabinet` puts on the plunger twang, and for the same reason.
      draw: live ? this.plungerDraw(now, plunger) : 0,
      plungerLive: live,
    }
    const was = this.controlsWas
    if (
      was !== undefined &&
      was.left === next.left &&
      was.right === next.right &&
      was.plungerLive === next.plungerLive &&
      Math.abs(was.draw - next.draw) < 0.005
    ) {
      return
    }
    this.controlsWas = next
    report(next)
  }

  private plungerDraw(now: number, startedAt: number | null): number {
    if (this.touchDraw > 0) return this.touchDraw
    if (startedAt === null) return 0
    const held = now - startedAt
    return held <= 0 ? 0 : Math.min(1, held / 800)
  }

  /** Paint the DMD strip, if the layout has one. */
  private attachDmd(strip: HTMLCanvasElement): void {
    const ctx = strip.getContext('2d', { alpha: false }) as Canvas2DLike | null
    if (ctx === null) return
    this.dmdContext = ctx
    const palette = this.session.loaded.definition.display?.palette
    this.dmd = dmdPainter(ctx, palette, (width, height) => {
      const scratch = document.createElement('canvas')
      scratch.width = width
      scratch.height = height
      const scratchCtx = scratch.getContext('2d') as Canvas2DLike | null
      if (scratchCtx === null) throw new Error('dmd strip: the browser refused a 2-D context')
      return { ctx: scratchCtx, source: scratch }
    })
  }

  /**
   * One line for the player, gone in `NOTICE_MS`.
   *
   * **One mechanism rather than two.** This was `stepNotice`, private to the graphics step-down,
   * and the recording cap wanted the same thing — a sentence, once, that expires. A second field
   * beside it would have meant a second expiry to get wrong and a `??` in the readout deciding
   * which of two one-off lines wins. Last one wins, which is what "one-off" means.
   */
  /**
   * The session is beginning a game on the pristine simulation and is about to record the press
   * that begins it at step 0. Everything this runtime remembers about the previous game goes now,
   * **before** that press is recorded — a recorder reset one frame later lost it, and with it any
   * chance of the trace re-simulating.
   */
  /**
   * What this game was played with, for the leaderboard filter 06 §2.1 asks for (M9-38).
   *
   * **It said `keyboard` for every game ever played in a headset.** The line it replaces was
   * `this.touched ? 'touch' : 'keyboard'`, evaluated at game over — so the browser's whole WebXR
   * surface, both schemes of it, has been submitting scores under the flat client's label since
   * replays existed, and `xr-hands` and `xr-controllers` are two of the seven values on the
   * contract with no producer anywhere in the product.
   *
   * **XR wins over touch, and touch over the keyboard**, because that is the order of specificity
   * rather than a preference: a headset player who tapped the screen before entering VR did not
   * play a touch game, and a keyboard is what is left when nothing else happened. The two XR
   * schemes are separated by which had the ball for longer, which is the only reading of "played
   * with hands" that survives a player picking up a controller half way through.
   */
  private inputMethodPlayed(): InputMethod {
    const { hands, controllers } = this.xrFrames
    if (hands > 0 || controllers > 0) return hands >= controllers ? 'xr-hands' : 'xr-controllers'
    return this.touched ? 'touch' : 'keyboard'
  }

  private onNewGame(): void {
    this.recorder?.reset()
    this.wasOver = false
    this.typedInitials = ''
    this.skippedInitials = false
    // How this game was played is a fact about *this* game. `touched` was never cleared, so a
    // player who tapped once on their first game submitted every later game in the session as
    // `touch` — the M8-68 shape again, one field along.
    this.touched = false
    this.xrFrames = { hands: 0, controllers: 0 }
    // `reset` clears the recorder's own cap, so the notice has to be sayable again — a second
    // two-hour game deserves the same warning as the first.
    this.saidCapped = false
    this.savedAt = Date.now()
    this.audio?.newGame()
  }

  private sayOnce(sentence: string, now: number): void {
    this.oneOff = sentence
    this.oneOffUntil = now + NOTICE_MS
  }

  /**
   * Hand the HUD what changed.
   *
   * Guarded by a cheap signature rather than published every frame: a React tree re-rendered
   * sixty times a second for a score that changed twice is most of the cost of the shell, and
   * the numbers a player reads change far more slowly than the picture does.
   */
  private publish(now: number, plunger: number | null): void {
    const report = this.options.onReadout
    if (report === undefined) return
    // The interruption is a **state**, not an event: it is true for exactly as long as the
    // machine is stopped, and `PlaySession` clears it the moment the player resumes. Copying it
    // into a field of our own — as this did — kept a stopped-machine notice on screen over a
    // machine that had been running again for ten minutes.
    const interruption = this.session.interruption
    const game = this.session.game
    const player = game.players[game.currentPlayer]
    const readout: MachineReadout = {
      score: player?.score ?? 0,
      ball: game.currentBall,
      ballsPerGame: game.ballsPerGame,
      player: game.currentPlayer + 1,
      players: game.players.length,
      phase: game.phase,
      awaitingLaunch: game.awaitingLaunch,
      plunger: this.plungerDraw(now, plunger),
      tilted: player?.tilted === true,
      ballSaveSeconds: this.session.ballSaveSeconds,
      initials: game.initialsText,
      initialsAt: game.phase === 'highScoreEntry' ? game.initialsPosition : -1,
      caption: this.audio?.caption ?? '',
      standings: game.players.map((p): PlayerStanding => ({
        player: p.index + 1,
        score: p.score,
        tilted: p.tilted,
        ballsPlayed: p.ballsPlayed,
      })),
      notice: interruption !== '' ? interruption : this.oneOff,
      // Asked of the driver rather than tracked here. A second copy of "does this machine have a
      // display" is a second thing that can be wrong, and it would be wrong in the direction that
      // hides the only score on screen.
      hasDisplay: this.session.display.present,
      audible: this.audible,
      audioMuted: this.audioPrefs.muted,
      paused: this.paused,
      micMuted: this.voice.microphone,
      voiceMuted: this.voice.others,
      micAccess: this.voice.access,
      micNoticed: this.voice.state.noticed,
    }
    const signature = [
      readout.score,
      readout.ball,
      readout.player,
      readout.phase,
      readout.awaitingLaunch,
      Math.round(readout.plunger * 20),
      readout.tilted,
      Math.round(readout.ballSaveSeconds * 4),
      readout.initials,
      readout.initialsAt,
      readout.caption,
      // Every player's score, not just the current one's: in a hot-seat game the interesting
      // change is often somebody *else's* row, and a signature that ignored them would leave a
      // scoreboard frozen on the numbers it had when the current player took over.
      readout.standings.map((p) => `${p.player}:${p.score}:${p.tilted ? 't' : ''}`).join(','),
      readout.notice,
      readout.hasDisplay,
      readout.audible,
      readout.audioMuted,
      readout.paused,
      readout.micMuted,
      readout.voiceMuted,
      readout.micAccess,
      readout.micNoticed,
    ].join('|')
    if (signature === this.lastReadout) return
    this.lastReadout = signature
    report(readout)
  }
}

/**
 * How often an interrupted game is written down, in wall-clock milliseconds.
 *
 * Ten seconds, which is the most play a crash can cost. Shorter would write a growing document
 * more often than it changes usefully; longer starts to lose a ball.
 */
const RESUME_SAVE_MS = 10_000

/** The seed a session uses when nobody names one. Kept beside the runtime that passes it on. */
const DEFAULT_SEED = 20260817

/** What a table row says when the score did not qualify for initials. */
const NO_INITIALS = '---'

/**
 * What goes in the browser's own score list for one finished game.
 *
 * Three cases and they are genuinely different: the player typed three characters, the player was
 * asked and declined, or the score never qualified and nobody was asked at all. The last two read
 * the same to somebody looking at the list — a score with no name on it — which is why
 * `SKIPPED_INITIALS` and `NO_INITIALS` are the same string; they are still computed apart,
 * because only one of them is a thing the player chose.
 */
export function finalInitials(typed: string, skipped: boolean): string {
  if (skipped) return SKIPPED_INITIALS
  return typed === '' ? NO_INITIALS : typed
}

/**
 * How long a one-off notice stays on screen.
 *
 * Long enough to read a sentence twice, short enough that a player who looked away does not come
 * back to a machine that appears to be reporting a problem it is not having.
 */
const NOTICE_MS = 8000

/**
 * What a player is told when their game passes the two-hour recording cap.
 *
 * **Says the consequence, not the mechanism.** "The trace is capped" is a sentence about
 * `MAX_TRACE_STEPS`; what the player has actually lost is the leaderboard and the resume, and
 * those are the two things worth interrupting them to say. Exported so a test can assert the
 * sentence and so the Quest client's toast can be held to the same words by eye — a toast is not
 * a menu label, so nothing mechanical holds them together.
 */
export const RECORDING_CAPPED =
  'No leaderboard or resume for this game - two-hour recording limit, play carries on'

/**
 * The device-pixel ratio, capped.
 *
 * Two, not what the phone claims. A strip 128 dots wide drawn at ratio 3 is a 1,200-pixel buffer
 * for 128 dots of information, and nearest-neighbour upscaling looks identical past the point
 * where one dot covers more than a couple of physical pixels.
 */
function dpr(): number {
  const ratio = (globalThis as { devicePixelRatio?: number }).devicePixelRatio
  return typeof ratio === 'number' && ratio > 0 ? Math.min(ratio, 2) : 1
}

/**
 * The player's ears, in the machine's own frame (M9-48).
 *
 * Two things it will not do. It will not invent a head — an immersive frame that carries no
 * viewer pose is a frame the runtime itself refuses to place anything against, and the mixer
 * gets the same answer it gives a flat screen. And it will not compute the transform: that is
 * `MachineScene.listenerAt`'s, because the group it asks is the group the emitters are drawn in.
 *
 * The forward comes from `forwardFromYaw` when the runtime gave a yaw and no vector, which is
 * every caller written before M5-33 — the same fallback `XRRuntime.advance` takes, so the
 * listener faces where the placement thinks the player is facing.
 */
function headInMachine(
  view: MachineView,
  head: XRFrameInput['head'],
): { x: number; y: number; z: number; fx: number; fy: number; fz: number } | undefined {
  if (head === undefined) return undefined
  const forward = head.forward ?? forwardFromYaw(head.yaw, head.pitch ?? 0)
  return view.scene.listenerAt(head.x, head.y, head.z, forward.x, forward.y, forward.z)
}

/** `XRRuntime`'s three recycle options, from the build's setting. */
function xrSafety(): { recycle: boolean; recycleAfterSeconds?: number; recycleWarnSeconds?: number } {
  const setting = recycleSetting()
  return {
    recycle: setting.recycle,
    ...(setting.afterSeconds === undefined ? {} : { recycleAfterSeconds: setting.afterSeconds }),
    ...(setting.warnSeconds === undefined ? {} : { recycleWarnSeconds: setting.warnSeconds }),
  }
}
